import React, { useEffect, useMemo, useState } from "react";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import AppShell from "../components/AppShell";
import EntityLink from "../components/EntityLink";
import ProjectSplitChip from "../components/ProjectSplitChip";
import ProjectPicker, { formatProjectLabel } from "../components/ProjectPicker";
import ManualAttendanceDialog from "../components/ManualAttendanceDialog";
import BulkAttendanceDialog from "../components/BulkAttendanceDialog";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { ROLES, canRecordManualAttendance } from "../lib/rbac";
import { useHasPermission } from "../contexts/PermissionsContext";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { formatDate, formatDateTime, statusBadgeClass } from "../lib/domain";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle, Plus, RefreshCw, ClipboardCheck, Trash2, TriangleAlert, Radio, MoreVertical, RotateCcw, ShieldAlert, Pencil, Download, FileText, FileSpreadsheet, Zap, StickyNote, Check, X } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import EditAttendanceDialog from "../components/EditAttendanceDialog";
import OTEntryDialog from "../components/OTEntryDialog";
import OTRemoveDialog from "../components/OTRemoveDialog";
import SplitByProjectsDialog from "../components/SplitByProjectsDialog";
import {
    OfficerChangeDiffPopover, BulkApproveReviewDialog,
} from "../components/OfficerChangeDiff";
import EarlyDepartureNoteDialog from "../components/EarlyDepartureNoteDialog";
import BulkEditAttendanceDialog from "../components/BulkEditAttendanceDialog";
import {
    ActiveShiftRuleBanner, buildWorkedTooltip,
} from "../components/ShiftRuleBanner";
import AdjustHoursDialog from "../components/AdjustHoursDialog";
import { Label } from "../components/ui/label";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import ConfirmDialog from "../components/ConfirmDialog";
import ModuleContextChip from "../components/ModuleContextChip";
import DateInput from "../components/DateInput";
import MapBiometricDialog from "../components/MapBiometricDialog";
import ModuleIdChip from "../components/ModuleIdChip";
import WorkerTypeBadge, { deriveWorkerCategory } from "../components/WorkerTypeBadge";
import EmploymentStatusBadge from "../components/EmploymentStatusBadge";
import { Link2 } from "lucide-react";

const ALL = "__all__";
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

// Small info badge next to Clock In / Clock Out times when the underlying event
// was auto-classified by the server (att=0 face-only scan). Tooltip explains it.
function AutoClassifiedBadge({ classifiedBy, testid }) {
    if (classifiedBy !== "auto_pair" &&
        classifiedBy !== "auto_time_of_day" &&
        classifiedBy !== "auto_first_of_day") return null;
    const tooltip = "Auto-classified — first scan of day = Check-In. Subsequent scans alternate (2nd = Check-Out, 3rd = Check-In, …). Reclassify via ⋮ menu if incorrect.";
    return (
        <span
            data-testid={testid}
            title={tooltip}
            className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blue-50 text-blue-600 text-[9px] font-bold cursor-help align-middle"
            aria-label="Auto-classified"
        >
            i
        </span>
    );
}

// Iter48 + Iter60.p19 — Source pill for a daily row. Shows one of:
//   - "MANUAL"    (amber)   — source_override="manual" OR any classifier is manual
//   - "MIXED"     (sky)     — one classifier is manual, the other is a biometric source
//   - "BIOMETRIC" (emerald) — no manual flag anywhere, and the row has clock times
//                              (default fallback for populated rows — covers TEST
//                              fixtures where classifiers are null but times exist)
//   - "—"                    — no clock times at all (absent day / empty row)
// Every rendered row shows *something*; empty cells were the p19 defect.
function SourcePill({ row }) {
    // Iter60.p41b — Synthetic-absent rows (no backing DB doc, no events)
    // wear a distinct AUTO chip so operators can tell them apart from
    // real BIOMETRIC / MANUAL absences.
    if (row.synthetic) {
        return (
            <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider bg-slate-500 text-white"
                title="Auto-generated — no biometric or manual events on this day. Add a manual entry to record a scan."
                data-testid={`att-source-auto-${row.employee_id}`}
            >
                AUTO
            </span>
        );
    }
    // Explicit override wins outright.
    if (row.source_override === "manual") {
        return (
            <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider bg-amber-500 text-white"
                title={`Manually adjusted${row.last_edited_by_name ? ` · last by ${row.last_edited_by_name}` : ""}`}
                data-testid={`att-source-manual-${row.id}`}
            >
                MANUAL
            </span>
        );
    }
    const inCb = row.first_clock_in_classified_by;
    const outCb = row.last_clock_out_classified_by;
    const inManual = inCb === "manual";
    const outManual = outCb === "manual";
    const inBiom = inCb && inCb !== "manual";
    const outBiom = outCb && outCb !== "manual";
    // One clock came from the device, the other was typed by hand.
    if ((inManual && outBiom) || (outManual && inBiom)) {
        return (
            <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider bg-sky-600 text-white"
                title="Mixed — one clock came from the biometric device, the other was typed manually"
                data-testid={`att-source-mixed-${row.id}`}
            >
                MIXED
            </span>
        );
    }
    // Any manual classifier at all → MANUAL.
    if (inManual || outManual) {
        return (
            <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider bg-amber-500 text-white"
                title="Manually entered"
                data-testid={`att-source-manual-${row.id}`}
            >
                MANUAL
            </span>
        );
    }
    // Populated row with no manual flag anywhere → default to BIOMETRIC. This
    // covers TEST fixtures and legacy rows that pre-date `classified_by`.
    if (row.first_clock_in || row.last_clock_out) {
        return (
            <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider bg-emerald-600 text-white"
                title="Biometric — IN/OUT captured by the device"
                data-testid={`att-source-biometric-${row.id}`}
            >
                BIOMETRIC
            </span>
        );
    }
    // Absent day — nothing to attribute. Render a grey em-dash so the column
    // never looks empty (that's a UX bug, not "no data").
    return (
        <span
            className="text-[color:var(--asl-muted)] text-xs"
            title="No attendance recorded"
            data-testid={`att-source-none-${row.id}`}
        >
            —
        </span>
    );
}

// Iter60.p19 — Manual detection helper (spec: source_override=="manual" OR
// either classifier=="manual"). Used to gate the "Replace with Biometric"
// and "Delete Manual Entry" row actions on the client.
function isManualRow(row) {
    if (!row) return false;
    if (row.source_override === "manual") return true;
    return row.first_clock_in_classified_by === "manual" || row.last_clock_out_classified_by === "manual";
}

// Iter60.p19 — Role gate for the two reconcile actions. Backend enforces
// module scope for dept managers; the frontend only checks role membership.
function canReconcile(user) {
    if (!user) return false;
    return (
        user.role === "kaafi_system_admin" ||
        user.role === "kaafi_hr_manager" ||
        user.role === "asl_hr_officer" ||
        user.role === "asl_department_manager"
    );
}

// Iter60.p54.3 — Adjust Hours parity for the Attendance Officer. The
// three reconcile roles PLUS the Attendance Officer can add hour
// adjustments. The revert-on-edit workflow in `_apply_adjustment_internal`
// (backend) auto-transitions approved rows the Officer touches back to
// `submitted` so the Manager can re-review the diff.
function canAdjustHours(user) {
    if (!user) return false;
    return canReconcile(user) || user.role === "asl_attendance_officer";
}

const STATUS_LABEL = {
    present: "Present", late: "Late", absent: "Absent", partial: "Partial",
    exception: "Exception", weekend: "Weekend", holiday: "Holiday",
    paid_leave: "Paid Leave", unpaid_leave: "Unpaid Leave",
    // Iter72.1 — Canonical leave status. Payroll/attendance now emit
    // `on_leave` with a companion `is_paid_leave` boolean on the row;
    // the badge label stays "On Leave" — the drawer surfaces the paid
    // vs unpaid distinction via the linked leave record.
    on_leave: "On Leave",
    pending: "Pending",
    // Iter60.p27 — new status when an employee worked on a weekend day.
    weekend_present: "Weekend Present",
};

function fmtTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Smart worked-hours formatter.
//   - < 60 min          → "Xm"
//   - ≥ 60 min, ends 0m → "Xh"
//   - otherwise         → "Xh Ym"
// If the shift is still in progress (checked-in, not checked-out yet) OR there
// is no attendance at all, show "—" instead of a misleading "0m".
function fmtWorked(row) {
    const mins = Math.max(0, Math.round(row?.worked_minutes ?? 0));
    const inProgress = !!row?.first_clock_in && !row?.last_clock_out;
    if (inProgress) return <span title="Check-out not yet recorded" className="text-[color:var(--asl-muted)]">in progress</span>;
    if (mins === 0) return "—";
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins - h * 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Iter60.p21 — Minutes formatter for the Payable column. Handles the "no
// worked time AND no adjustment" case as a soft em-dash.
function fmtMinutes(mins) {
    const n = Math.max(0, Math.round(mins ?? 0));
    if (n === 0) return "—";
    if (n < 60) return `${n}m`;
    const h = Math.floor(n / 60);
    const m = n - h * 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Iter60.p21 — Per-row cell rendering the stack of adjustment chips. Each
// chip is coloured by sign (green = +, crimson = −), has a tooltip
// showing note + actor + timestamp, and a hover-only × to remove.
function AdjustmentChips({ row, canRemove, onRemove }) {
    const adjs = row.adjustments || [];
    if (adjs.length === 0) {
        return <span className="text-[color:var(--asl-muted)] text-xs">—</span>;
    }
    return (
        <div className="flex flex-wrap gap-1" data-testid={`att-adj-cell-${row.id}`}>
            {adjs.map((a) => {
                const pos = a.minutes > 0;
                const stamp = a.applied_at ? new Date(a.applied_at).toISOString().slice(0, 16).replace("T", " ") : "";
                const title = `"${a.note}" — ${a.applied_by_email || "system"}, ${stamp}`;
                return (
                    <span
                        key={a.id}
                        title={title}
                        className={`group inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold mono ${
                            pos
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-rose-100 text-[color:var(--asl-crimson)]"
                        }`}
                        data-testid={`att-adj-chip-${a.id}`}
                    >
                        {pos ? "+" : ""}{a.minutes}m
                        {canRemove && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onRemove && onRemove(a); }}
                                className="ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:text-black"
                                aria-label={`Remove ${a.minutes} minute adjustment`}
                                data-testid={`att-adj-chip-remove-${a.id}`}
                            >
                                ×
                            </button>
                        )}
                    </span>
                );
            })}
        </div>
    );
}

export default function Attendance() {
    const { user } = useAuth();
    const { modules, byId: modulesById } = useModules();
    const scopedId = useScopedModuleId();
    const { moduleCode: pathModuleCode } = useParams();
    const canApprove = useHasPermission("attendance.approve_event", scopedId || null);
    const canApproveDaily = useHasPermission("attendance.approve_daily", scopedId || null);
    const canRecompute = useHasPermission("attendance.recompute", scopedId || null);
    const canDeleteAttendance = useHasPermission("attendance.delete_event", scopedId || null);
    const canEditAttendance = useHasPermission("attendance.write_manual", scopedId || null);
    // Iter60.p50 — Attendance Officer had `employees.view` stripped.
    // The `/attendance/daily` endpoint now hydrates `employee_name`
    // server-side, so we skip the `/employees` fetch entirely when the
    // user lacks the perm. Prevents a spurious 403 toast on page load.
    const canViewEmployeesList = useHasPermission("employees.view", scopedId || null);
    const canManual = canRecordManualAttendance(user);
    const canPurgeAttendance = user?.role === ROLES.KAAFI_ADMIN || user?.role === ROLES.KAAFI_HR;
    const [manualOpen, setManualOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Iter70 — Filter defaults. Extracted so both the initial state and the
    // "Clear filters" reset can share a single source of truth. Anything that
    // matches these values gets stripped from the URL by useFilterUrlSync so
    // shareable/bookmarkable URLs stay compact.
    const _defaultFrom = todayIso();
    const _defaultTo = todayIso();
    // Iter91 — Simplified filter set (5 user-facing filters: Employee /
    // Type / Project / Status / Worked, plus the From/To range and
    // Module scope). Removed state keys: `source`, `excused`,
    // `ot_status`, `include_absents`, row-state `status`. Kept hidden:
    // `has_note` + `employee_id` (drill-down from Home dashboard;
    // honoured from URL, surfaced via inline chips above the grid).
    //
    // `status` is REPURPOSED: previously held row-state values
    // (present / late / absent / …) which drove the backend `status`
    // param; now holds ONE of {"__all__", "pending", "approved"} and
    // drives the `approved: bool` param. This matches the new UX
    // ("All / Pending / Approved") and collapses the Approved
    // dropdown into Status.
    const FILTER_DEFAULTS = useMemo(() => ({
        module_id: scopedId || ALL,
        status: ALL,
        date_from: _defaultFrom,
        date_to: _defaultTo,
        project_id: ALL,
        search: "",
        employee_type: ALL,
        worked_state: ALL,
        has_note: false,
        employee_id: ALL,
    }), [scopedId, _defaultFrom, _defaultTo]);

    const [filters, setFilters] = useState(() => {
        // Iter70 — Translate URL sentinel "all" back to the internal
        // ALL constant so we don't ship a bogus `status=all` string to
        // the load() helper (which uses `!== ALL` to decide whether to
        // pass the param through to the API).
        const _sentinel = (raw) => (!raw || raw === "all") ? ALL : raw;
        // Iter91 — Legacy URL params for `approved`/`stage` map into the
        // new unified Status filter: approved=yes OR stage=approved →
        // "approved"; approved=no OR stage=pending → "pending".
        const _legacyStage = (() => {
            const stage = searchParams.get("stage");
            if (stage === "approved") return "approved";
            if (stage === "pending") return "pending";
            const approved = searchParams.get("approved");
            if (approved === "yes" || approved === "true") return "approved";
            if (approved === "no" || approved === "false") return "pending";
            return ALL;
        })();
        return {
            module_id: scopedId || ALL,
            status: _sentinel(searchParams.get("status")) !== ALL
                ? _sentinel(searchParams.get("status"))
                : _legacyStage,
            // Iter60.p30 (Task D) — Default FROM/TO to today. Operators
            // land on today's roster first; date range widens on demand.
            // Iter60.p43.3 — Also honour URL params for `date_from`,
            // `date_to`, `has_early_departure_note`, and `employee_id` so
            // the Home dashboard drill-down modal can land on the exact
            // filtered slice.
            // Iter70 — Support both legacy (`date_from`/`date_to`/
            // `has_early_departure_note`) AND the new short-form (`from`/
            // `to`/`has_note`) URL keys on initial hydration for
            // back-compat with existing Home drill-down links.
            date_from: searchParams.get("date_from") || searchParams.get("from") || _defaultFrom,
            date_to: searchParams.get("date_to") || searchParams.get("to") || _defaultTo,
            project_id: _sentinel(searchParams.get("project_id") || searchParams.get("project")),
            // Iter60.p30 (Task D) — Employee search box. Matches
            // full_name / employee_code / module_employee_id /
            // biometric_user_id case-insensitively on the backend.
            search: searchParams.get("search") || "",
            // Iter91 — New dropdowns. Values:
            //   employee_type: ALL / "casual" / "non_casual"
            //   worked_state:  ALL / "in_progress" / "worked"
            // Both are FE post-filters against the hydrated row shape
            // (`r.employment_type`, `r.first_clock_in` + `r.last_clock_out`
            // + `r.worked_minutes`) — no backend param, no new endpoint.
            employee_type: _sentinel(searchParams.get("type")),
            worked_state: _sentinel(searchParams.get("worked")),
            // Iter91 — `has_note` is now a HIDDEN filter. No checkbox in
            // the bar; still hydrated from URL so the Home dashboard
            // drill-down keeps working. When active, an inline chip
            // above the table surfaces it + exposes a × to clear.
            has_note: searchParams.get("has_early_departure_note") === "true"
                      || searchParams.get("has_note") === "true",
            // Iter60.p43.3 — Employee-specific drill-down (from the Home
            // "Early Departures This Week" widget). One-way: read from URL
            // on mount, no reverse sync so the operator can clear it via
            // the existing "Clear filters" affordance OR the Iter91
            // dedicated clear-chip above the table.
            employee_id: searchParams.get("employee_id") || ALL,
        };
    });
    const [showDeleted, setShowDeleted] = useState(() => searchParams.get("show_deleted") === "true");
    const [projects, setProjects] = useState([]);
    const [rows, setRows] = useState([]);
    const [employees, setEmployees] = useState({});
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [generateOpen, setGenerateOpen] = useState(false);
    const [purgeOpen, setPurgeOpen] = useState(false);
    const [genPhrase, setGenPhrase] = useState("");
    const [purgePhrase, setPurgePhrase] = useState("");
    const [busy, setBusy] = useState(false);
    // Iter13 — bulk delete/restore/purge state for daily rows.
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
    const [confirmBulkRestore, setConfirmBulkRestore] = useState(false);
    // Iter60.p34 — Bulk Unapprove confirmation dialog state.
    const [confirmBulkUnapprove, setConfirmBulkUnapprove] = useState(false);
    const [dailyPurgeOpen, setDailyPurgeOpen] = useState(false);
    const [dailyPurgePhrase, setDailyPurgePhrase] = useState("");
    // Iter48 — Editable attendance rows (single + bulk)
    const [editRow, setEditRow] = useState(null);
    // Iter60.p40 — Compact "Split by projects" dialog owns its own row
    // state so it doesn't share triggers/rendering with the full Edit modal.
    const [splitRow, setSplitRow] = useState(null);
    // Iter60.p43 — Early-departure note compact dialog.
    const [edNoteRow, setEdNoteRow] = useState(null);
    // Iter61 — Authorized OT dialogs (single row + bulk).
    const [otSingleRow, setOtSingleRow] = useState(null);
    const [otBulkOpen, setOtBulkOpen] = useState(false);
    // Iter88 — Remove-OT dialog state (holds the row whose OT entries
    // we're inspecting). Opened from the OVERTIME menu's new sibling
    // item; see `showRemoveOT` below.
    const [otRemoveRow, setOtRemoveRow] = useState(null);
    // Iter60.p41 — Cache the active shift rule(s) for the currently
    // visible module so the row-level Worked tooltip can render the
    // "Elapsed X · Break Ym (paid)" copy without a per-row fetch. The
    // banner primes this via its `onRulesLoaded` callback.
    const [activeShiftRules, setActiveShiftRules] = useState(null);
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    // Iter60.p19 — Manual↔Biometric reconcile state.
    const [replaceRow, setReplaceRow] = useState(null);
    const [deleteRow, setDeleteRow] = useState(null);
    const [bulkReplaceOpen, setBulkReplaceOpen] = useState(false);
    // Iter60.p21 — Attendance hour adjustments (Construction only).
    // Iter60.p23 — casual + non-casual both eligible now.
    // We keep `adjustRowIds` as the list to apply to (single or bulk). When
    // opened from a row kebab, it's `[row.id]`; from the bulk toolbar, it's
    // the set of currently-selected Construction rows.
    const [adjustRowIds, setAdjustRowIds] = useState(null);
    // Fix E — Live polling. Persisted per-user in localStorage, paused when tab hidden.
    const [live, setLive] = useState(() => {
        try { return localStorage.getItem("attendance.live") === "1"; } catch { return false; }
    });
    const [lastSyncedAt, setLastSyncedAt] = useState(null);
    // Iter60.p46 Bug B — UX guardrail for live-polling. Track the
    // timestamp of the last keystroke in ANY filter input so the 30s
    // tick can bail out mid-typing (avoids visual flicker + request
    // races with the user's typing). Focus check on the search input
    // covers the "user paused typing" case too.
    const keystrokeAtRef = React.useRef(0);
    const _shouldSkipLiveTick = React.useCallback(() => {
        const now = Date.now();
        if (now - (keystrokeAtRef.current || 0) < 300) return true;
        const el = typeof document !== "undefined" ? document.activeElement : null;
        if (el && el.tagName === "INPUT" && el.getAttribute("data-testid") === "attendance-search-input") {
            return true;
        }
        return false;
    }, []);
    useEffect(() => {
        const onKey = (e) => {
            const t = e?.target;
            if (t && (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA")) {
                keystrokeAtRef.current = Date.now();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;
    // Iter60.p19 — Manual↔Biometric reconcile is available to the same
    // three roles that own the operational HR flow. Dept-mgr scope is
    // enforced on the backend for the row's module.
    const canRecon = canReconcile(user);
    // Iter60.p21 — Attendance hour adjustments (Construction module only).
    // Iter60.p23 — dropped the casual-only restriction so ALL Construction
    // employees (casual + non-casual) can have their hours adjusted.
    // Iter60.p54.3 — Attendance Officer parity: they can now add hour
    // adjustments too (see `canAdjustHours` above). Reverting the row
    // approved→submitted is enforced server-side in `_apply_adjustment_internal`.
    // canAdjust: RBAC gate; adjustRowIds: opens the AdjustHoursDialog with
    // the set of currently-selected Construction rows.
    const canAdjust = canAdjustHours(user);
    // Iter61 — Authorized-OT: Admin, HR Manager, HR Officer, Dept
    // Manager. Officer (`asl_attendance_officer`) is NOT permitted.
    const canAddOT = useMemo(() => {
        const role = user?.role;
        return role === "kaafi_system_admin"
            || role === "kaafi_hr_manager"
            || role === "asl_hr_officer"
            || role === "asl_department_manager";
    }, [user?.role]);
    const cnModuleId = useMemo(
        () => (modules.find((m) => m.code === "CN") || {}).id || null,
        [modules],
    );
    const isCnRow = (row) => {
        if (!row || !cnModuleId) return false;
        // Iter60.p54.3 — Fall back to `row.module_id` when the
        // employees map is empty (Attendance Officer skips the
        // /employees prefetch per p50). The row itself carries the
        // module id, and the seed guarantees Officer scope = CN.
        if (row.module_id) {
            return row.module_id === cnModuleId;
        }
        const e = employees[row.employee_id];
        if (!e) return false;
        return e.module_id === cnModuleId;
    };

    const handleRemoveAdjustment = async (row, adj) => {
        // Lightweight confirm — window.confirm is enough for a low-stakes
        // reversible action; the audit log records the removal either way.
        const label = adj.minutes > 0 ? `+${adj.minutes}m` : `${adj.minutes}m`;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Remove this adjustment (${label})?\n\n"${adj.note}"`)) return;
        try {
            await api.delete(`/attendance/daily/${row.id}/adjustments/${adj.id}`);
            toast.success(`Removed ${label} adjustment.`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const load = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        const params = { date_from: filters.date_from, date_to: filters.date_to };
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        // Iter91 — Status filter is now "all / pending / approved" and
        // ONLY drives the backend `approved` boolean. Row-state status
        // (present / late / absent / …) is no longer in the UI; the
        // backend `status` param is left untouched for external callers.
        if (filters.status === "pending") params.approved = false;
        else if (filters.status === "approved") params.approved = true;
        if (filters.project_id !== ALL) {
            // Iter91 — ProjectPicker now emits "__unassigned__" for the
            // dedicated Unassigned option. Map to the backend's
            // `project_id="none"` sentinel which the list endpoint
            // already understands (returns rows with null/missing
            // `project_id`).
            params.project_id = filters.project_id === "__unassigned__"
                ? "none"
                : filters.project_id;
        }
        // Iter91 — `has_note` is hidden but still honoured for the Home
        // dashboard drill-down. When true, backend applies the early-
        // departure-note filter; frontend renders a clear chip above the
        // table so the operator sees why the view is pre-filtered.
        if (filters.has_note) params.has_early_departure_note = true;
        // Iter60.p43.3 — Employee drill-down filter (from Home widget).
        if (filters.employee_id && filters.employee_id !== ALL) {
            params.employee_id = filters.employee_id;
        }
        // Iter60.p30 (Task D) — Employee search. Backend matches
        // full_name / employee_code / module_employee_id /
        // biometric_user_id case-insensitively. Empty string skips
        // the filter entirely.
        if (filters.search && filters.search.trim()) {
            params.search = filters.search.trim();
        }
        if (showDeleted) params.include_deleted = true;
        try {
            const { data } = await api.get("/attendance/daily", { params });
            setRows(data);
            const empIds = [...new Set(data.map((r) => r.employee_id))];
            if (empIds.length && canViewEmployeesList) {
                const emp = await api.get("/employees", { params: { page_size: 200 } });
                const map = {};
                emp.data.items.forEach((e) => { map[e.id] = e; });
                setEmployees(map);
            }
            if (!silent) setSelected(new Set());
            setLastSyncedAt(new Date());
        } catch (e) {
            if (!silent) toast.error(formatApiError(e));
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filters.module_id, filters.status, filters.date_from, filters.date_to, filters.project_id, filters.search, filters.has_note, filters.employee_id, filters.employee_type, filters.worked_state, showDeleted]);

    // Iter70 — Two-way URL sync for ALL filter keys. On mount we hydrate any
    // params present in the URL (initial useState already reads legacy keys
    // for back-compat with Home drill-down links); reverse-sync writes are
    // debounced 300ms so rapid typing in the search box doesn't spam history
    // entries. Keys equal to defaults are stripped from the URL so shareable
    // links stay tidy. Legacy `has_early_departure_note`/`date_from`/`date_to`
    // params are preserved on first hydration but written back as the short
    // Iter70 keys (`has_note`/`from`/`to`) on the next filter change.
    //
    // Sentinel translation: our filter state uses `ALL = "__all__"` for
    // "no filter". Iter70's spec asks for the URL to use the friendly
    // literal "all" instead (e.g. `?status=all&source=all`). We map both
    // directions with per-key codecs so URL bookmarks stay readable AND
    // the internal load() logic keeps its `filters.status !== ALL` check.
    const _allCodec = {
        toUrl: (v) => (v === ALL ? "" : v),
        fromUrl: (raw) => (raw === "all" || raw === "" ? ALL : raw),
    };
    useFilterUrlSync({
        filters: { ...filters, show_deleted: showDeleted },
        defaults: { ...FILTER_DEFAULTS, show_deleted: false },
        paramMap: {
            date_from: "from",
            date_to: "to",
            search: "search",
            // Iter91 — Unified Status filter — 1-way "stage" alias kept for
            // backwards compatibility with existing shared URLs.
            status: {
                param: "stage",
                toUrl: (v) => (v === "approved" ? "approved" : v === "pending" ? "pending" : ""),
                fromUrl: (v) => (v === "approved" ? "approved" : v === "pending" ? "pending" : ALL),
            },
            project_id: { param: "project", ..._allCodec },
            // Iter91 — New dropdowns persisted on the URL under short keys.
            employee_type: { param: "type", ..._allCodec },
            worked_state: { param: "worked", ..._allCodec },
            has_note: "has_note",
            show_deleted: "show_deleted",
        },
        hydrate: (patch) => {
            const { show_deleted, ...rest } = patch;
            if (Object.keys(rest).length > 0) {
                setFilters((f) => ({ ...f, ...rest }));
            }
            if (show_deleted !== undefined) setShowDeleted(!!show_deleted);
        },
    });

    // Iter70 — Reset all filters to their defaults (and clear the URL).
    const clearAllFilters = React.useCallback(() => {
        setFilters(FILTER_DEFAULTS);
        setShowDeleted(false);
        // Immediately strip every managed param from the URL — the debounced
        // reverse-sync would eventually do this, but users expect the URL to
        // update instantly when they click "Clear filters".
        const next = new URLSearchParams(searchParams);
        [
            // Iter91 — Active URL keys for the simplified filter set.
            "from", "to", "search", "project", "stage", "type", "worked",
            "has_note", "show_deleted",
            // Legacy / dropped filter keys — clear these too so a
            // subsequent Back navigation (or a shared old URL) doesn't
            // re-hydrate stale state.
            "status", "approved", "source", "excused", "ot_status",
            "include_absents", "date_from", "date_to", "project_id",
            "has_early_departure_note", "employee_id",
        ].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });
    }, [FILTER_DEFAULTS, searchParams, setSearchParams]);

    // Iter50 + Iter59.p6 — Save-then-refetch callback for Manual / Bulk dialogs.
    //
    // Iter59.p6 policy change: on a range save we now AUTOMATICALLY widen the
    // visible FROM/TO to cover the created range instead of just showing an
    // info toast asking the user to change it themselves. Previously the
    // toast fired inconsistently (and users reported "the rows never appear"
    // because the default filter is today-only and their created range was
    // often in the past or future week). Widening is safe: we only ever grow
    // the window, never shrink it, so existing rows in view stay visible.
    const handleAttendanceSaved = React.useCallback(async (payload) => {
        const rng = payload?.createdRange;
        if (rng && rng.created && rng.from && rng.to) {
            // Widen the filter first, then reload — the useEffect on
            // filters.date_from/to already triggers `load()` so we avoid a
            // double-fetch here.
            const filterFrom = filters.date_from;
            const filterTo = filters.date_to;
            const newFrom = rng.from < filterFrom ? rng.from : filterFrom;
            const newTo = rng.to > filterTo ? rng.to : filterTo;
            const widened = newFrom !== filterFrom || newTo !== filterTo;
            if (widened) {
                setFilters((f) => ({ ...f, date_from: newFrom, date_to: newTo }));
                toast.success(
                    `Created ${rng.created} row${rng.created === 1 ? "" : "s"} · showing ${newFrom} → ${newTo}`,
                    { duration: 4500 },
                );
                // useEffect on filters will refetch — early-return so we
                // don't hit the endpoint twice.
                return;
            }
            // Range already covered by the current filter: just refresh +
            // give the user positive confirmation of the count.
            toast.success(
                `Created ${rng.created} row${rng.created === 1 ? "" : "s"} · showing ${filterFrom} → ${filterTo}`,
                { duration: 4500 },
            );
        }
        await load();
    }, [filters.date_from, filters.date_to]);

    // Fetch projects for the scoped module (for the filter dropdown + label lookup).
    useEffect(() => {
        if (!scopedId) { setProjects([]); return; }
        api.get("/projects", { params: { module_id: scopedId } })
            .then(({ data }) => setProjects(Array.isArray(data) ? data : (data.items || [])))
            .catch(() => setProjects([]));
    }, [scopedId]);

    // Iter70 — The ad-hoc project_id URL sync (previously here) has been
    // consolidated into `useFilterUrlSync` above, which handles all filter
    // keys uniformly.

    const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

    // Fix E — Live polling: 30s interval while `live` is ON, paused when tab hidden.
    // Iter60.p46 (Bug B) — Also depend on the FULL filter set + the
    // `load` reference so the setInterval callback captures the
    // freshest closure. Without this, `filters.search` (and other
    // narrow-scope filters that don't appear in the deps below) get
    // dropped from the request 30s after they were typed — the user
    // sees their search evaporate as the poll re-fires the stale
    // closure. UX guardrail: `_shouldSkipLiveTick` bails out when the
    // search input is focused or the user typed within the last 300ms.
    useEffect(() => {
        try { localStorage.setItem("attendance.live", live ? "1" : "0"); } catch { /* ignore */ }
        if (!live) return;
        let timer = null;
        const tick = () => {
            if (document.visibilityState !== "visible") return;
            if (_shouldSkipLiveTick()) return;
            load({ silent: true });
        };
        timer = setInterval(tick, 30000);
        const onVis = () => {
            // When the tab becomes visible again, do an immediate silent refresh
            // so the operator sees fresh data without waiting a full interval.
            if (document.visibilityState === "visible" && !_shouldSkipLiveTick()) {
                load({ silent: true });
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            if (timer) clearInterval(timer);
            document.removeEventListener("visibilitychange", onVis);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live, filters.module_id, filters.status,
        filters.date_from, filters.date_to, filters.project_id, filters.search,
        filters.has_note, filters.employee_id,
        filters.employee_type, filters.worked_state, showDeleted]);

    // Bug 1 fix — Attendance cache invalidation.
    // A mutation on another page (Exception Queue reclassify/map/approve/reject,
    // Users purge, etc.) writes `attendance.invalidated_at` to localStorage. This
    // effect listens for:
    //   (1) `storage` events (cross-tab: mutation happens in tab B, refetch tab A)
    //   (2) `visibilitychange` becoming visible (same-tab switch: user tabs back)
    //   (3) `focus` (belt-and-braces for browsers that skip visibilitychange)
    // Any of these that carries a timestamp newer than our last successful load
    // triggers a silent refetch. The Live-toggle polling above stays independent.
    useEffect(() => {
        const key = "attendance.invalidated_at";
        const shouldRefetch = () => {
            try {
                const stamp = parseInt(localStorage.getItem(key) || "0", 10);
                const lastMs = lastSyncedAt ? lastSyncedAt.getTime() : 0;
                return stamp > lastMs;
            } catch { return false; }
        };
        const onStorage = (e) => { if (e.key === key && shouldRefetch()) load({ silent: true }); };
        const onVis = () => { if (document.visibilityState === "visible" && shouldRefetch()) load({ silent: true }); };
        const onFocus = () => { if (shouldRefetch()) load({ silent: true }); };
        window.addEventListener("storage", onStorage);
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onFocus);
        // Also check IMMEDIATELY on mount — user navigated /exceptions → /attendance
        // via in-app link, so localStorage was bumped but no visibility/focus event fires.
        if (shouldRefetch()) load({ silent: true });
        return () => {
            window.removeEventListener("storage", onStorage);
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastSyncedAt?.getTime(), filters.module_id, filters.status, filters.date_from, filters.date_to]);

    const approveOne = async (r) => {
        try {
            await api.post(`/attendance/daily/${r.id}/approve`);
            toast.success("Approved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Iter60.p54.2 — Bulk approve now branches:
    //   * If ANY selected row is `stage=submitted` → open the review
    //     dialog so the Manager can inspect Officer changes per row
    //     and Skip individual rows.
    //   * If ALL selected rows are plain `pending` (no diff to
    //     review) → hit the endpoint directly, no dialog.
    // The dialog itself renders a "Direct approve" section for any
    // Pending rows mixed in with the Submitted ones.
    const [bulkApproveReview, setBulkApproveReview] = useState({
        open: false, submitted: [], pending: [], busy: false,
    });
    const openBulkApproveReview = () => {
        const submitted = [];
        const pending = [];
        filteredRows.forEach((r) => {
            if (!r.id || r.deleted_at || r.approved) return;
            if (!selected.has(r.id)) return;
            const st = r.stage || (r.approved ? "approved" : "pending");
            if (st === "submitted") submitted.push(r);
            else if (st === "pending") pending.push(r);
        });
        return { submitted, pending };
    };
    const approveBulk = async () => {
        if (selectedRealIds.length === 0) return;
        const { submitted, pending } = openBulkApproveReview();
        if (submitted.length > 0) {
            // Review dialog path — Manager confirms each Officer diff.
            setBulkApproveReview({ open: true, submitted, pending, busy: false });
            return;
        }
        // No submitted rows in the selection — direct approve.
        try {
            const res = await api.post("/attendance/daily/approve-bulk", { ids: selectedRealIds });
            toast.success(`Approved ${res.data.approved} record(s)`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Confirm handler for the review dialog. Iterates the included ids.
    const runReviewedBulkApprove = async (ids) => {
        if (!ids || !ids.length) {
            setBulkApproveReview((s) => ({ ...s, open: false }));
            return;
        }
        setBulkApproveReview((s) => ({ ...s, busy: true }));
        try {
            const res = await api.post("/attendance/daily/approve-bulk", { ids });
            toast.success(`Approved ${res.data.approved} record(s)`);
            setBulkApproveReview({ open: false, submitted: [], pending: [], busy: false });
            setSelected(new Set());
            load();
        } catch (e) {
            toast.error(formatApiError(e));
            setBulkApproveReview((s) => ({ ...s, busy: false }));
        }
    };

    // Iter60.p34 — Bulk Unapprove. Flips approved=false on selected
    // rows; times/status/adjustments/etc are preserved server-side.
    const unapproveBulk = async () => {
        // approvedSelectedIds is guarded by the button visibility, but
        // double-check here so a stale selection doesn't hit the API.
        const ids = approvedSelectedIds;
        if (!ids.length) return;
        try {
            const res = await api.post("/attendance/daily/bulk-unapprove", {
                row_ids: ids,
                reason: "manual_ui_unapprove",
            });
            const un = (res.data.unapproved || []).length;
            const sk = (res.data.skipped || []).length;
            const er = (res.data.errors || []).length;
            let msg = `Unapproved ${un} row${un === 1 ? "" : "s"}`;
            if (sk) msg += ` · ${sk} skipped`;
            if (er) msg += ` · ${er} error${er === 1 ? "" : "s"}`;
            if (un > 0) toast.success(msg); else toast.warning(msg);
            setConfirmBulkUnapprove(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Iter60.p34 — single-row unapprove (row kebab menu). No confirm —
    // the row is only touched if it is currently approved.
    const unapproveOne = async (r) => {
        if (!r?.id || !r?.approved) return;
        try {
            await api.post(`/attendance/daily/${r.id}/unapprove`);
            toast.success("Unapproved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Iter60.p54 — Officer submit-for-approval workflow.
    // `submittedCount` powers the Manager's crimson "Ready for my
    // approval: N (Officer)" chip. Auto-refreshes on load() and after
    // every approve / unapprove / submit action.
    const [submittedCount, setSubmittedCount] = useState(null);
    const [showSubmittedOnly, setShowSubmittedOnly] = useState(false);

    const isOfficer = user?.role === "asl_attendance_officer";
    const isManagerLike = ["kaafi_system_admin", "kaafi_hr_manager",
        "asl_hr_officer", "asl_department_manager"].includes(user?.role);

    // Iter60.p54.2 — Officer's `Submit` gate. The client-side helper
    // is retained as a fallback for legacy rows that haven't been
    // re-hydrated yet, but the authoritative signal is
    // `r.officer_can_submit` (computed server-side per request in
    // list_daily). That backend flag enforces THREE conditions:
    //   (a) current user is an Officer
    //   (b) row.stage === "pending"
    //   (c) row.last_clock_out is not null  ← p54.2 addition
    //   (d) `_officer_touched_row(row, user)` — officer authored the row
    const officerCanSubmit = (r) => {
        if (r?.officer_can_submit === true) return true;
        if (r?.officer_can_submit === false) return false;
        // Legacy fallback — pre-p54.2 rows won't carry the flag.
        if (!isOfficer || !r?.id || r?.synthetic) return false;
        const stage = r.stage || (r.approved ? "approved" : "pending");
        if (stage !== "pending") return false;
        if (!r.last_clock_out) return false;
        return r.created_by === user?.id || r.last_edited_by === user?.id;
    };

    const submitOne = async (r) => {
        try {
            await api.post(`/attendance/daily/${r.id}/submit`);
            toast.success("Submitted for approval");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitSelected = async () => {
        // Iter60.p54.1 — iterate `filteredRows` so the count matches
        // the visible submittable set. `selected` is already narrowed
        // to visible IDs by the prune effect; this is belt & braces.
        const ids = filteredRows
            .filter((r) => selected.has(r.id) && officerCanSubmit(r))
            .map((r) => r.id);
        if (!ids.length) return;
        try {
            const { data } = await api.post("/attendance/daily/submit-bulk", { row_ids: ids });
            const n = (data.submitted || []).length;
            toast.success(`Submitted ${n} row${n === 1 ? "" : "s"} for approval`);
            setSelected(new Set());
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitAllMine = async () => {
        const modId = scopedId || (filters.module_id !== ALL ? filters.module_id : null);
        if (!modId) {
            toast.error("Pick a specific module first");
            return;
        }
        try {
            const { data } = await api.post(
                "/attendance/daily/submit-all-mine", { module_id: modId },
            );
            const n = (data.submitted || []).length;
            toast.success(
                n
                    ? `Submitted ${n} pending row${n === 1 ? "" : "s"} for approval`
                    : "No pending rows of yours to submit.",
            );
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Fetch the officer-submitted count for the Manager's chip.
    useEffect(() => {
        const modId = scopedId || (filters.module_id !== ALL ? filters.module_id : null);
        if (!isManagerLike || !modId) {
            setSubmittedCount(null);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(
                    "/attendance/daily/submitted-count",
                    { params: { module_id: modId } },
                );
                setSubmittedCount(typeof data?.count === "number" ? data.count : 0);
            } catch (e) {
                setSubmittedCount(null);
            }
        })();
    }, [isManagerLike, scopedId, filters.module_id, rows.length, lastSyncedAt?.getTime?.()]);

    const generateToday = async () => {
        if (genPhrase !== "GENERATE") return;
        const params = { work_date: todayIso() };
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/biometric-mock/generate-sample", params);
            toast.success(`Test data inserted: ${data.created} events, ${data.absent} absent, ${data.exceptions} exceptions`);
            setGenerateOpen(false);
            setGenPhrase("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const purgeTestData = async () => {
        if (purgePhrase !== "DELETE TEST DATA") return;
        const params = {};
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        params.date_from = filters.date_from;
        params.date_to   = filters.date_to;
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/test-generated/purge", null, { params });
            toast.success(`Purged ${data.deleted_events} test events; reset ${data.reset_daily_rows} daily row(s)`);
            setPurgeOpen(false);
            setPurgePhrase("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const recompute = async () => {
        try {
            const { data } = await api.post("/attendance/recompute", {
                module_id: filters.module_id !== ALL ? filters.module_id : null,
                date_from: filters.date_from, date_to: filters.date_to,
            });
            toast.success(`Recomputed ${data.computed} day(s)`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Iter59.p9 — Attendance export (Excel + PDF). Uses the SAME filter set
    // as the daily list so what the user sees is exactly what they export.
    const [exportBusy, setExportBusy] = useState(null); // "xlsx" | "pdf" | null
    const exportAttendance = async (kind) => {
        const params = { date_from: filters.date_from, date_to: filters.date_to };
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        // Iter91 — Unified Status filter drives the backend `approved`
        // boolean (same mapping as `load()`). Row-state status (present/
        // late/absent/…) no longer exists in the UI.
        if (filters.status === "pending") params.approved = false;
        else if (filters.status === "approved") params.approved = true;
        if (filters.project_id !== ALL) {
            params.project_id = filters.project_id === "__unassigned__"
                ? "none"
                : filters.project_id;
        }
        if (filters.search && filters.search.trim()) {
            params.search = filters.search.trim();
        }
        // Iter91 — Hidden drill-down filters are preserved on export so
        // the Home dashboard flow (Early Departures → export) still
        // produces the pre-filtered file users expect.
        if (filters.has_note) params.has_early_departure_note = true;
        if (filters.employee_id && filters.employee_id !== ALL) {
            params.employee_id = filters.employee_id;
        }
        if (showDeleted) params.include_deleted = true;
        // Iter91 — Known gap: Type + Worked are frontend-only post-filters.
        // The exported file reflects the server-side slice; it does NOT
        // apply those two filters. Toast the user so there's no silent
        // mismatch between on-screen rows and the download. Parked as
        // Iter91b (server-side Type / Worked params) in PRD backlog.
        if ((filters.employee_type && filters.employee_type !== ALL)
            || (filters.worked_state && filters.worked_state !== ALL)) {
            toast.info(
                "Note: Type and Worked filters apply to the on-screen grid but not the exported file.",
                { duration: 6000 },
            );
        }
        setExportBusy(kind);
        try {
            const path = kind === "pdf" ? "/attendance/export.pdf" : "/attendance/export.xlsx";
            const resp = await api.get(path, { params, responseType: "blob" });
            const mime = kind === "pdf"
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const blob = new Blob([resp.data], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const disposition = resp.headers?.["content-disposition"] || "";
            const m = disposition.match(/filename="?([^";]+)"?/);
            const scope = filters.module_id !== ALL
                ? (modulesById[filters.module_id]?.code || "module").toLowerCase()
                : "all";
            const df = (filters.date_from || "").replace(/-/g, "") || "all";
            const dt = (filters.date_to || "").replace(/-/g, "") || "all";
            a.download = m?.[1] || `attendance_${scope}_${df}_${dt}.${kind}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Attendance ${kind.toUpperCase()} exported`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setExportBusy(null);
        }
    };

    // Iter13 — Bulk delete / restore / purge handlers for attendance daily rows.
    // Iter60.p54.1a — All three use `selectedRealIds` so synthetic
    // rowKeys (added by header select-all for banner accuracy) don't
    // hit the API.
    const runBulkDelete = async () => {
        if (selectedRealIds.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/daily/bulk-delete", {
                daily_ids: selectedRealIds,
                mode: "soft",
            });
            const msg = `${data.soft_deleted} record${data.soft_deleted === 1 ? "" : "s"} deleted. `
                + `You can restore them via 'Show deleted'.`;
            toast.success(msg);
            if (data.skipped && data.skipped.length > 0) {
                toast.info(`${data.skipped.length} row(s) skipped — see console for reasons.`);
                console.warn("attendance bulk-delete skipped:", data.skipped);
            }
            setSelected(new Set());
            setConfirmBulkDelete(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const runBulkRestore = async () => {
        if (selectedRealIds.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/daily/bulk-restore", {
                daily_ids: selectedRealIds,
            });
            toast.success(`${data.restored} record${data.restored === 1 ? "" : "s"} restored.`);
            if (data.skipped && data.skipped.length > 0) {
                toast.info(`${data.skipped.length} row(s) skipped.`);
                console.warn("attendance bulk-restore skipped:", data.skipped);
            }
            setSelected(new Set());
            setConfirmBulkRestore(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const runBulkPurge = async () => {
        if (selectedRealIds.length === 0 || dailyPurgePhrase !== "PURGE") return;
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/daily/bulk-purge", {
                daily_ids: selectedRealIds,
                confirm: "PURGE",
            });
            toast.success(`Permanently purged ${data.purged} record${data.purged === 1 ? "" : "s"} and ${data.events_purged} scan event${data.events_purged === 1 ? "" : "s"}.`);
            setSelected(new Set());
            setDailyPurgeOpen(false);
            setDailyPurgePhrase("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const clearSelection = () => setSelected(new Set());

    // Iter60.p54 — Manager can toggle "Ready for my approval: N
    // (Officer)" chip to filter the grid to submitted rows only. The
    // filter runs client-side against the already-loaded `rows` so
    // there is no extra API round-trip.
    // Iter60.p54.1 — every downstream "selection" derivation MUST
    // iterate `filteredRows`, not `rows`. `rows` is backend-filtered
    // (date / status / project / …) but does NOT include the
    // client-side "Ready for my approval" narrowing. Iterating `rows`
    // caused select-all to select 68 unfiltered rows even when only 1
    // was visible.
    const filteredRows = useMemo(() => {
        let out = rows;
        if (showSubmittedOnly) {
            out = out.filter((r) => {
                const st = r.stage || (r.approved ? "approved" : "pending");
                return st === "submitted";
            });
        }
        // Iter91 — Type (Casual / Non-Casual) frontend post-filter.
        // Every row is hydrated with `employment_type` by the list
        // endpoint (see attendance.py:_hydrate loop). No backend param,
        // no new endpoint. Values map directly to the badge.
        if (filters.employee_type && filters.employee_type !== ALL) {
            out = out.filter((r) => r.employment_type === filters.employee_type);
        }
        // Iter91 — Worked state (In progress / Hours worked) FE post-filter.
        //   * "in_progress" → `first_clock_in` present AND `last_clock_out` empty
        //   * "worked"      → `worked_minutes > 0` (recompute invariant also
        //                     guarantees `last_clock_out` is set when wm > 0)
        if (filters.worked_state && filters.worked_state !== ALL) {
            out = out.filter((r) => {
                const ci = r.first_clock_in;
                const co = r.last_clock_out;
                const wm = Number(r.worked_minutes || 0);
                if (filters.worked_state === "in_progress") {
                    return !!ci && !co;
                }
                if (filters.worked_state === "worked") {
                    return wm > 0;
                }
                return true;
            });
        }
        return out;
    }, [rows, showSubmittedOnly, filters.employee_type, filters.worked_state]);

    // Iter60.p58 — Summary reflects the VISIBLE grid so RECORDS matches
    // exactly what the user is looking at. Previously read `rows`, but
    // the Manager "Ready for my approval" chip and any client-side
    // narrowing was invisible in the KPIs. Reading `filteredRows`
    // keeps counters honest as filters flip.
    const summary = useMemo(() => {
        const out = { total: filteredRows.length, approved: 0, exceptions: 0, late: 0 };
        filteredRows.forEach((r) => {
            if (r.approved) out.approved += 1;
            if (r.status === "exception") out.exceptions += 1;
            if (r.status === "late") out.late += 1;
        });
        return out;
    }, [filteredRows]);
    // Iter60.p54.1 — Prune the selection whenever the visible set
    // changes so bulk actions can never touch rows the user isn't
    // currently looking at. Applies to the client-side "Ready for
    // my approval" chip AND to every backend filter transition
    // (module / status / date / project / …) — `filteredRows`
    // recomputes on both. Behavior chosen: **Option A (re-evaluate)**
    // per bug spec — previously-selected rows that go out of view are
    // dropped from the selection set immediately, and do NOT come
    // back when the filter is widened again.
    useEffect(() => {
        const visible = new Set(filteredRows.map((r) => r.id || `syn-${r.employee_id}-${r.work_date}`));
        setSelected((prev) => {
            if (prev.size === 0) return prev;
            let changed = false;
            const next = new Set();
            prev.forEach((key) => {
                if (visible.has(key)) next.add(key);
                else changed = true;
            });
            return changed ? next : prev;
        });
    }, [filteredRows]);

    // Iter60.p54.1a — Root cause of the "37 visible / 4 selected"
    // regression report: `visibleIds` still carried the p41b filter
    // `!r.synthetic && r.id`, so on the synth-heavy single-day view
    // (33 synthetic absent rows + 4 real biometric rows) the header
    // select-all only added the 4 real IDs to `selected` and the
    // banner mismatched the visible grid. Fix: switch the selection
    // scheme to `rowKey` (which always exists — `r.id` for real rows,
    // `syn-{emp}-{day}` for synthetic ones, matching the React key on
    // each <tr>). Now header select-all covers every rendered row
    // and the banner naturally matches. Bulk-action buttons compute
    // their own eligibility counts (approve/edit/delete only touch
    // real rows via `liveSelectedIds` et al. — those memos check
    // `selected.has(r.id)` which returns false for synthetic rows
    // whose `r.id` is null, so bulk actions still skip them).
    const rowKeyOf = (r) => r.id || `syn-${r.employee_id}-${r.work_date}`;

    // Bug 4 — master "Select all" checkbox for the currently rendered rows.
    // Iter60.p54.1a — Now includes synthetic rows too. Selection is
    // keyed by `rowKey`, not by `id`.
    const visibleKeys = useMemo(
        () => filteredRows.map(rowKeyOf),
        [filteredRows],
    );
    const selectedVisibleCount = useMemo(
        () => visibleKeys.reduce((n, k) => n + (selected.has(k) ? 1 : 0), 0),
        [visibleKeys, selected],
    );
    const allVisibleSelected = visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
    const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
    const toggleAllVisible = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                visibleKeys.forEach((k) => next.delete(k));
            } else {
                visibleKeys.forEach((k) => next.add(k));
            }
            return next;
        });
    };

    // Iter60.p54.1a — Real DB IDs currently in `selected`. Synthetic
    // rows contribute rowKeys like `syn-{emp}-{day}` to `selected`
    // (so the banner count matches the visible grid) but they must
    // be filtered out before any API call that expects real ids.
    // Backend endpoints (`approve-bulk`, `bulk-delete`, etc.) would
    // just return them under `skipped` — filtering here keeps the
    // network payload lean and the success/skip counts honest.
    const selectedRealIds = useMemo(
        () => filteredRows.filter((r) => !r.synthetic && r.id && selected.has(r.id)).map((r) => r.id),
        [filteredRows, selected],
    );

    // Iter13 — Selection breakdown: how many selected rows are live vs deleted.
    // Drives contextual visibility of the bulk bar's action buttons.
    // Iter60.p54.1 — iterate `filteredRows` so counts reflect only
    // what the user is currently looking at (see prune-on-filter
    // effect above; `selected` is already narrowed, this is belt &
    // braces so a stale reference to a hidden row can never surface).
    const selectedBreakdown = useMemo(() => {
        let live = 0, deleted = 0;
        filteredRows.forEach((r) => {
            if (!selected.has(r.id)) return;
            if (r.deleted_at) deleted += 1; else live += 1;
        });
        return { live, deleted, total: live + deleted };
    }, [filteredRows, selected]);
    // Iter48 — the ids of the currently-selected LIVE rows (not deleted).
    // Passed into the Bulk Edit dialog.
    const liveSelectedIds = useMemo(
        () => filteredRows.filter((r) => selected.has(r.id) && !r.deleted_at).map((r) => r.id),
        [filteredRows, selected],
    );
    // Iter60.p19 — the ids of the currently-selected LIVE Manual rows.
    // These are the only rows the "Replace with Biometric" bulk button
    // can act on. Non-manual rows in the selection are skipped by the
    // backend anyway; we filter here so the button count is accurate.
    const manualSelectedIds = useMemo(
        () => filteredRows.filter((r) => selected.has(r.id) && !r.deleted_at && isManualRow(r)).map((r) => r.id),
        [filteredRows, selected],
    );
    // Iter60.p21 — the ids of the currently-selected LIVE Construction
    // rows. Same rationale: the backend skips ineligible rows, but the UI
    // hides the bulk button when NONE of the selection is eligible, and
    // reflects the accurate count in the button label.
    // Iter60.p23 — non-casual Construction rows are now eligible too.
    const cnSelectedIds = useMemo(
        () => filteredRows.filter((r) => selected.has(r.id) && !r.deleted_at && isCnRow(r)).map((r) => r.id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filteredRows, selected, employees, cnModuleId],
    );
    // Iter60.p34 — ids of currently-selected LIVE APPROVED rows. Only
    // these can be flipped by the Bulk Unapprove button. Backend also
    // skips non-approved rows in the response (returns them under
    // `skipped`), but the button count reflects the exact live subset.
    const approvedSelectedIds = useMemo(
        () => filteredRows.filter((r) => selected.has(r.id) && !r.deleted_at && r.approved).map((r) => r.id),
        [filteredRows, selected],
    );

    return (
        <AppShell title="Daily Attendance">
            <ManualAttendanceDialog
                open={manualOpen}
                onOpenChange={setManualOpen}
                defaultModuleId={filters.module_id !== ALL ? filters.module_id : undefined}
                defaultWorkDate={filters.date_from}
                onSaved={handleAttendanceSaved}
            />
            <BulkAttendanceDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                moduleId={filters.module_id !== ALL ? filters.module_id : (scopedId || null)}
                defaultWorkDate={filters.date_from}
                onSaved={handleAttendanceSaved}
            />
            {/* Iter48 — Single-row edit dialog */}
            <EditAttendanceDialog
                open={!!editRow}
                onOpenChange={(o) => !o && setEditRow(null)}
                row={editRow}
                onSaved={() => { setEditRow(null); load(); }}
                // Iter60.p21 — Adjustments section only mounts when the row is
                // Construction AND the current user can adjust. The dialog
                // stays dumb about eligibility rules. Iter60.p23 — casual +
                // non-casual both allowed.
                adjustments={
                    editRow && canAdjust && isCnRow(editRow)
                        ? {
                            onAdd: () => {
                                const rid = editRow.id;
                                setEditRow(null);
                                setAdjustRowIds([rid]);
                            },
                            onRemove: (adj) => handleRemoveAdjustment(editRow, adj),
                        }
                        : null
                }
            />
            {/* Iter60.p40 — Compact Split by projects dialog. Uses the SAME
                PATCH /attendance/daily/{id} endpoint as the full Edit modal,
                sending only `project_allocations` in the body. */}
            <SplitByProjectsDialog
                open={!!splitRow}
                onOpenChange={(o) => !o && setSplitRow(null)}
                row={splitRow}
                moduleCode={pathModuleCode}
                onSaved={() => { setSplitRow(null); load(); }}
            />
            {/* Iter60.p43 — Compact Early-Departure Note dialog. Same-day
                mount so the toast + row refresh happen atomically. Uses
                its own endpoint (`/attendance/daily/{id}/early-departure`)
                so audit trail records the note-specific action separately
                from generic row PATCHes. */}
            <EarlyDepartureNoteDialog
                open={!!edNoteRow}
                row={edNoteRow}
                canEdit={canEditAttendance}
                onClose={() => setEdNoteRow(null)}
                onSaved={() => { setEdNoteRow(null); load(); }}
            />
            {/* Iter61 — Authorized OT entry dialog (single row). */}
            <OTEntryDialog
                open={!!otSingleRow}
                onOpenChange={(o) => !o && setOtSingleRow(null)}
                daily={otSingleRow}
                onSaved={() => { setOtSingleRow(null); load(); }}
            />
            {/* Iter61 — Authorized OT entry dialog (bulk). */}
            <OTEntryDialog
                open={otBulkOpen}
                onOpenChange={setOtBulkOpen}
                bulk={{
                    moduleId: filters.module_id !== ALL ? filters.module_id : (scopedId || null),
                    defaultDate: filters.date_from,
                    // Iter61 hotfix — `employees` here is a Map (id → object).
                    // The dialog iterates the roster so pass its values.
                    employees: Array.isArray(employees)
                        ? employees
                        : Object.values(employees || {}),
                }}
                onSaved={() => { setOtBulkOpen(false); load(); }}
            />
            {/* Iter88 — Remove-OT dialog (per-entry or remove-all). */}
            <OTRemoveDialog
                open={!!otRemoveRow}
                row={otRemoveRow}
                onClose={() => setOtRemoveRow(null)}
                onSaved={() => { setOtRemoveRow(null); load(); }}
            />
            {/* Iter48 — Bulk edit dialog */}
            {bulkEditOpen && (
                <BulkEditAttendanceDialog
                    open={bulkEditOpen}
                    onOpenChange={setBulkEditOpen}
                    ids={liveSelectedIds}
                    moduleId={filters.module_id !== ALL ? filters.module_id : (scopedId || null)}
                    onSaved={() => { setSelected(new Set()); load(); }}
                />
            )}
            {/* Iter60.p54.2 — Manager review dialog for bulk approve
                on Officer-submitted rows. Renders one card per
                Submitted row with the pending_changes diff + a Skip
                toggle. Pending rows in the same selection are shown
                as a compact "Direct approve" section — no review
                needed for those. */}
            <BulkApproveReviewDialog
                open={bulkApproveReview.open}
                onOpenChange={(v) => setBulkApproveReview((s) => ({ ...s, open: v }))}
                submittedRows={bulkApproveReview.submitted}
                pendingRows={bulkApproveReview.pending}
                busy={bulkApproveReview.busy}
                onConfirm={runReviewedBulkApprove}
            />
            {/* Iter60.p19 — Replace-with-Biometric confirmation (single row) */}
            {replaceRow && (
                <ReplaceWithBiometricDialog
                    row={replaceRow}
                    onClose={() => setReplaceRow(null)}
                    onDone={() => { setReplaceRow(null); load(); }}
                />
            )}
            {/* Iter60.p19 — Delete Manual Entry confirmation (single row) */}
            {deleteRow && (
                <DeleteManualDialog
                    row={deleteRow}
                    onClose={() => setDeleteRow(null)}
                    onDone={() => { setDeleteRow(null); load(); }}
                />
            )}
            {/* Iter60.p19 — Bulk replace-with-biometric confirmation */}
            {bulkReplaceOpen && (
                <BulkReplaceDialog
                    rowIds={manualSelectedIds}
                    onClose={() => setBulkReplaceOpen(false)}
                    onDone={() => { setBulkReplaceOpen(false); setSelected(new Set()); load(); }}
                />
            )}
            {/* Iter60.p21 — Adjust hours (single or bulk). `adjustRowIds`
                is [row.id] when opened from a kebab, or the selection ids
                when opened from the bulk toolbar. */}
            {adjustRowIds && (
                <AdjustHoursDialog
                    rowIds={adjustRowIds}
                    onClose={() => setAdjustRowIds(null)}
                    onDone={() => {
                        setAdjustRowIds(null);
                        if (adjustRowIds.length > 1) setSelected(new Set());
                        load();
                    }}
                />
            )}
            {/* Iter13 — Soft delete confirmation */}
            <ConfirmDialog
                open={confirmBulkDelete}
                onOpenChange={setConfirmBulkDelete}
                variant="destructive"
                title={`Delete ${selectedBreakdown.live} attendance record${selectedBreakdown.live === 1 ? "" : "s"}?`}
                description="These records will be marked as deleted and hidden from the attendance list. Any exceptions and daily approvals attached will be preserved for audit. You can restore them via the 'Show deleted' toggle."
                confirmLabel={`Delete ${selectedBreakdown.live} record${selectedBreakdown.live === 1 ? "" : "s"}`}
                busy={busy}
                onConfirm={runBulkDelete}
                testid="att-confirm-bulk-delete"
            />
            {/* Iter13 — Restore confirmation */}
            <ConfirmDialog
                open={confirmBulkRestore}
                onOpenChange={setConfirmBulkRestore}
                variant="info"
                title={`Restore ${selectedBreakdown.deleted} attendance record${selectedBreakdown.deleted === 1 ? "" : "s"}?`}
                description="These records will be brought back and their status will be recomputed from surviving scan events. Adjacent days are not touched."
                confirmLabel={`Restore ${selectedBreakdown.deleted} record${selectedBreakdown.deleted === 1 ? "" : "s"}`}
                busy={busy}
                onConfirm={runBulkRestore}
                testid="att-confirm-bulk-restore"
            />
            {/* Iter60.p34 — Bulk Unapprove confirmation. Explicitly
                highlights that only the approval flag is flipped —
                times / status / adjustments stay intact. */}
            <ConfirmDialog
                open={confirmBulkUnapprove}
                onOpenChange={setConfirmBulkUnapprove}
                variant="info"
                title={`Unapprove ${approvedSelectedIds.length} row${approvedSelectedIds.length === 1 ? "" : "s"}?`}
                description="Times and status will be preserved. Only the approval flag is removed. Payroll periods that used these rows will need a fresh approval cycle before payout."
                confirmLabel={`Unapprove ${approvedSelectedIds.length} row${approvedSelectedIds.length === 1 ? "" : "s"}`}
                busy={busy}
                onConfirm={unapproveBulk}
                testid="bulk-unapprove-confirm-dialog"
            />
            {/* Iter13 — Permanent purge dialog (typed PURGE guard) */}
            <Dialog open={dailyPurgeOpen} onOpenChange={(o) => { if (!o) { setDailyPurgeOpen(false); setDailyPurgePhrase(""); } }}>
                <DialogContent data-testid="att-daily-purge-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[color:var(--asl-crimson)]">
                            <ShieldAlert className="h-5 w-5" />
                            Permanently delete {selectedBreakdown.deleted} record{selectedBreakdown.deleted === 1 ? "" : "s"}?
                        </DialogTitle>
                        <DialogDescription>
                            This will permanently remove these attendance records <span className="font-semibold">and their underlying scan events</span>. This action cannot be undone and is logged for compliance. A full row snapshot is written to the audit log for forensic recovery.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="att-purge-phrase" className="text-xs">Type <span className="mono font-bold">PURGE</span> to confirm</Label>
                        <Input
                            id="att-purge-phrase"
                            value={dailyPurgePhrase}
                            onChange={(e) => setDailyPurgePhrase(e.target.value)}
                            placeholder="PURGE"
                            data-testid="att-daily-purge-phrase"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setDailyPurgeOpen(false); setDailyPurgePhrase(""); }} data-testid="att-daily-purge-cancel">
                            Cancel
                        </Button>
                        <Button
                            className="bg-[color:var(--asl-crimson)] text-white hover:brightness-95"
                            onClick={runBulkPurge}
                            disabled={busy || dailyPurgePhrase !== "PURGE"}
                            data-testid="att-daily-purge-confirm"
                        >
                            {busy ? "Purging…" : "Permanently delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <section className="space-y-5" data-testid="attendance-page">
                {/* Iter60.p41 — Active Shift Rule info banner. Hidden in
                    the master view (no single active rule to describe).
                    Copy explicitly states whether break is paid/unpaid
                    so anyone reviewing attendance can see the math. */}
                <ActiveShiftRuleBanner
                    moduleId={
                        scopedId
                        || (filters.module_id !== ALL ? filters.module_id : null)
                    }
                    onRulesLoaded={setActiveShiftRules}
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label="Records" value={summary.total} />
                    <Stat label="Approved" value={`${summary.approved} / ${summary.total}`} />
                    <Stat label="Late" value={summary.late} />
                    <Stat label="Exceptions" value={summary.exceptions} accent="rose" />
                </div>

                {/* Iter60.p50 — Attendance Officer expectation-setting.
                    They can Add / Edit / Adjust / Split / Note but cannot
                    Approve — the Construction Dept Manager finalizes. A
                    subtle amber note under the KPIs makes the workflow
                    obvious without cluttering the page. */}
                {user?.role === "asl_attendance_officer" && (
                    <div className="flex items-start gap-3 flex-wrap">
                        <div
                            className="text-[11px] px-3 py-2 rounded-md border flex-1 min-w-[280px]"
                            style={{
                                background: "rgba(217, 119, 6, 0.06)",
                                borderColor: "rgba(217, 119, 6, 0.35)",
                                color: "#b45309",
                            }}
                            data-testid="att-officer-pending-hint"
                        >
                            Rows you create or edit stay <span className="font-semibold">Pending</span> until the Construction Manager approves them. Click <span className="font-semibold">Submit</span> on each row (or use the toolbar) to send them for review.
                        </div>
                        <Button
                            size="sm"
                            className="asl-crimson-bg text-white hover:opacity-90 shrink-0"
                            onClick={submitAllMine}
                            data-testid="att-officer-submit-all-mine"
                            title="Submit every pending row you created or edited in this module."
                        >
                            Submit all my pending
                        </Button>
                    </div>
                )}

                {/* Iter60.p54 — Manager approval counter.
                    Renders a crimson pill "Ready for my approval: N (Officer)"
                    with a subtle/neutral variant when N=0. Clicking toggles
                    the filter to show only `submitted` rows. */}
                {isManagerLike && submittedCount !== null && (
                    <button
                        type="button"
                        onClick={() => setShowSubmittedOnly((v) => !v)}
                        className={`inline-flex items-center gap-2 self-start rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                            submittedCount > 0
                                ? "text-white hover:opacity-90"
                                : "text-[color:var(--asl-muted)] hover:opacity-90"
                        }`}
                        style={{
                            background: submittedCount > 0
                                ? "var(--asl-crimson)"
                                : "rgba(0,0,0,0.04)",
                            border: submittedCount > 0
                                ? "1px solid var(--asl-crimson)"
                                : "1px solid var(--asl-border)",
                            outline: showSubmittedOnly
                                ? "2px solid rgba(168, 50, 74, 0.35)"
                                : "none",
                            outlineOffset: 2,
                        }}
                        data-testid="att-mgr-submitted-chip"
                        aria-label={
                            submittedCount > 0
                                ? `${submittedCount} officer-submitted rows ready for approval — click to filter`
                                : "No officer submissions"
                        }
                    >
                        {submittedCount > 0
                            ? <>Ready for my approval: {submittedCount} <span className="opacity-80">(Officer)</span></>
                            : "No Officer submissions"}
                        {showSubmittedOnly && submittedCount > 0 ? (
                            <span className="text-[10px] uppercase tracking-wider">· filtered</span>
                        ) : null}
                    </button>
                )}

                <div className="asl-card p-4 flex flex-wrap items-end gap-3">
                    <FieldDate label="From" value={filters.date_from}
                        onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))} testid="att-date-from" />
                    <FieldDate label="To" value={filters.date_to}
                        onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))} testid="att-date-to" />
                    {/* Iter70 — Clear filters resets every managed filter to
                        its default AND strips the corresponding URL params
                        instantly (bypassing the 300ms sync debounce). */}
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={clearAllFilters}
                        data-testid="att-clear-filters"
                        title="Reset all filters to their defaults and clear the URL"
                        className="self-end"
                    >
                        Clear filters
                    </Button>
                    {/* Iter60.p30 (Task D) — Employee search chip.
                        Debounces to filters.search which drives the load
                        effect via /attendance/daily?search=…. Empty
                        string clears the filter. */}
                    <FieldSearch value={filters.search}
                        onChange={(v) => setFilters((f) => ({ ...f, search: v }))}
                        testid="attendance-search-input"
                        resultCount={filters.search ? rows.length : null} />
                    {/* Iter60.p13 — hide Module dropdown when inside a workspace. */}
                    {scopedId ? (
                        <ModuleContextChip moduleId={scopedId} testid="att-module-chip" />
                    ) : (
                        <FieldSel label="Module" value={filters.module_id}
                            onChange={(v) => setFilters((f) => ({ ...f, module_id: v }))}
                            items={[{ value: ALL, label: "All modules" }, ...modules.map((m) => ({ value: m.id, label: m.name }))]}
                            testid="att-module" />
                    )}
                    {/* Iter91 — Status: unified "All / Pending / Approved".
                        Collapses the former row-state Status dropdown AND
                        the standalone Approved dropdown into one control.
                        Backend param: `approved: bool`. */}
                    <FieldSel label="Status" value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        items={[
                            { value: ALL, label: "All" },
                            { value: "pending", label: "Pending" },
                            { value: "approved", label: "Approved" },
                        ]}
                        testid="att-status" />
                    {/* Iter91 — Type: Casual / Non-Casual. Reads
                        `r.employment_type` already hydrated on every
                        row by the list endpoint; FE post-filter, no
                        backend param. Same source as the TYPE badge
                        column so the filter is self-consistent with
                        what the user sees per row. */}
                    <FieldSel label="Type" value={filters.employee_type}
                        onChange={(v) => setFilters((f) => ({ ...f, employee_type: v }))}
                        items={[
                            { value: ALL, label: "All" },
                            { value: "casual", label: "Casual" },
                            { value: "non_casual", label: "Non-Casual" },
                        ]}
                        testid="att-type-filter" />
                    {scopedId && (
                        <div className="flex flex-col gap-1 min-w-[200px]">
                            <label className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                Project
                            </label>
                            {/* Iter67 F1 — Universal ProjectPicker with
                                Job # + name search. Iter91 — Added the
                                dedicated "Unassigned" option so operators
                                can slice to rows with no project assigned.
                                Sentinel "__unassigned__" maps to backend
                                `project_id="none"` at the load()/export
                                call sites. */}
                            <ProjectPicker
                                projects={projects}
                                value={filters.project_id === ALL ? "" : filters.project_id}
                                onValueChange={(v) => setFilters((f) => ({ ...f, project_id: v || ALL }))}
                                allowNone
                                noneLabel="— All projects —"
                                allowUnassigned
                                unassignedLabel="— Unassigned —"
                                placeholder="— All projects —"
                                testid="att-project"
                            />
                        </div>
                    )}
                    {/* Iter91 — Worked: All / In progress / Hours worked.
                        FE post-filter against
                        (first_clock_in, last_clock_out, worked_minutes).
                        "Hours worked" uses the strict `worked_minutes > 0`
                        bucket — adjustment-only rows are out of scope
                        (see Iter91 Phase 1 diagnosis). */}
                    <FieldSel label="Worked" value={filters.worked_state}
                        onChange={(v) => setFilters((f) => ({ ...f, worked_state: v }))}
                        items={[
                            { value: ALL, label: "All" },
                            { value: "in_progress", label: "In progress" },
                            { value: "worked", label: "Hours worked" },
                        ]}
                        testid="att-worked-filter" />
                    <div className="ml-auto flex items-center gap-2">
                        {canManual && (
                            <Button size="sm" className="asl-crimson-bg text-white" onClick={() => setManualOpen(true)} data-testid="att-manual-button">
                                <Plus className="h-4 w-4 mr-1" /> Add Manual
                            </Button>
                        )}
                        {canManual && (
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => setBulkOpen(true)} data-testid="att-bulk-button">
                                <Plus className="h-4 w-4 mr-1" /> Add attendance (bulk)
                            </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setGenerateOpen(true)}
                                data-testid="att-generate-sample"
                                className={isAdmin ? "border-amber-500 text-amber-800 hover:bg-amber-50" : "hidden"}>
                            <TriangleAlert className="h-4 w-4 mr-1 text-amber-600" />
                            Generate Test Attendance (dev only)
                        </Button>
                        {isAdmin && (
                            <Button size="sm" variant="outline" onClick={() => setPurgeOpen(true)}
                                    data-testid="att-purge-test"
                                    className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] hover:bg-[color:var(--asl-crimson-50,#fdecef)]">
                                <Trash2 className="h-4 w-4 mr-1" /> Purge Test Data
                            </Button>
                        )}
                        {canRecompute && (
                            <Button size="sm" variant="outline" onClick={recompute} data-testid="att-recompute">
                                <RefreshCw className="h-4 w-4 mr-1" /> Recompute
                            </Button>
                        )}
                        {/* Iter91 — "OT Review" relocated here from the
                            removed OT Status filter chip. Same navigation
                            target; visible to the same manager/officer
                            roles that previously saw the inline link via
                            `canAddOT` which gates the OT review flow in
                            lockstep with Bulk add OT. */}
                        {canAddOT && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(
                                    `/m/${pathModuleCode}/attendance/ot-review?from=${filters.date_from}&to=${filters.date_to}`,
                                )}
                                data-testid="att-ot-review-link"
                                className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] hover:bg-[color:var(--asl-crimson-50,#fdecef)]"
                                title="Open the HR OT-review queue (detected but unauthorized OT)"
                            >
                                <Zap className="h-4 w-4 mr-1" /> OT Review
                            </Button>
                        )}
                        {/* Iter61 — Bulk OT entry (manager-authorized). */}
                        {canAddOT && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setOtBulkOpen(true)}
                                data-testid="att-bulk-ot"
                                className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] hover:bg-[color:var(--asl-crimson-50,#fdecef)]"
                            >
                                <Zap className="h-4 w-4 mr-1" /> Bulk add OT
                            </Button>
                        )}
                        {/* Iter59.p9 — Attendance export dropdown (Excel + PDF). */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" data-testid="att-export-menu" disabled={!!exportBusy}>
                                    <Download className="h-4 w-4 mr-1" />
                                    {exportBusy ? `Exporting ${exportBusy.toUpperCase()}…` : "Export"}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel>Export current view</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => exportAttendance("xlsx")}
                                    data-testid="att-export-xlsx"
                                >
                                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                                    Excel (.xlsx)
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => exportAttendance("pdf")}
                                    data-testid="att-export-pdf"
                                >
                                    <FileText className="h-4 w-4 mr-2" />
                                    PDF (landscape)
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Iter13 — Show deleted toggle */}
                        {(canDeleteAttendance || canPurgeAttendance) && (
                            <ShowDeletedToggle
                                value={showDeleted}
                                onChange={(v) => { setShowDeleted(v); setSelected(new Set()); }}
                                testid="att-show-deleted-toggle"
                            />
                        )}
                        {/* Fix E — Live polling toggle (30s while tab visible). */}
                        <button
                            type="button"
                            onClick={() => setLive((v) => !v)}
                            data-testid="att-live-toggle"
                            title={live
                                ? "Live: auto-refreshing every 30 seconds while this tab is visible. Click to stop."
                                : "Off: click to auto-refresh every 30 seconds."}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                                live
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                    : "border-[color:var(--asl-border)] bg-white text-[color:var(--asl-muted)] hover:bg-gray-50"
                            }`}
                        >
                            <span className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`}
                                  aria-hidden="true" />
                            <Radio className="h-3.5 w-3.5" aria-hidden="true" />
                            <span className="font-semibold">{live ? "Live" : "Live"}</span>
                            {live && lastSyncedAt && (
                                <span className="mono text-[10px] text-emerald-700 ml-1" data-testid="att-live-lastsync">
                                    {lastSyncedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Iter13 — Sticky bulk action bar. Shows when at least one row is selected.
                    Buttons are contextual: live-only selections show Approve+Delete; deleted-only
                    selections show Restore+Purge; mixed selections show both banks with counts. */}
                {selected.size > 0 && (
                    <div
                        className="sticky top-14 z-20 flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm shadow-md text-white"
                        style={{ background: "var(--asl-navy)" }}
                        data-testid="att-bulk-action-bar"
                    >
                        <span className="font-semibold" data-testid="att-bulk-count">
                            {selected.size} attendance record{selected.size === 1 ? "" : "s"} selected
                            {/* Iter60.p54.1a — When synthetic (Absent-with-no-events)
                                rows are in the selection, surface how many are
                                actionable so the "Approve N" mismatch is
                                self-explanatory. */}
                            {selected.size > selectedRealIds.length
                                ? ` · ${selectedRealIds.length} actionable`
                                : ""}
                            {selectedBreakdown.deleted > 0 && selectedBreakdown.live > 0
                                ? ` (${selectedBreakdown.live} live · ${selectedBreakdown.deleted} deleted)`
                                : ""}
                        </span>
                        <div className="ml-auto flex items-center gap-2 flex-wrap">
                            {canApproveDaily && selectedBreakdown.live > 0 && (
                                <Button size="sm" className="bg-white text-[color:var(--asl-navy)] hover:bg-gray-100"
                                        onClick={approveBulk} data-testid="att-bulk-approve">
                                    <CheckCircle className="h-4 w-4 mr-1" /> Approve {selectedBreakdown.live}
                                </Button>
                            )}
                            {/* Iter60.p54 — Officer bulk submit. Count reflects
                                only rows the Officer touched (created / edited)
                                AND currently in `pending`. Backend re-checks
                                per row + fails fast on any foreign row.
                                Iter60.p54.2 — Also require the row's
                                `check_out` to be non-null. When SOME selected
                                rows don't meet the gate, the button renders
                                DISABLED with an explanatory tooltip instead
                                of vanishing — the user is looking straight
                                at their selection and needs to know why. */}
                            {isOfficer && (() => {
                                const submittableIds = filteredRows
                                    .filter((r) => selected.has(r.id) && officerCanSubmit(r))
                                    .map((r) => r.id);
                                // Officer-owned selected rows that are NOT yet
                                // ready (missing check-out OR not touched).
                                const notReadyCount = filteredRows.filter((r) => {
                                    if (!selected.has(r.id) || r.deleted_at) return false;
                                    if (r.synthetic || !r.id) return false;
                                    const st = r.stage || (r.approved ? "approved" : "pending");
                                    return st === "pending" && !officerCanSubmit(r);
                                }).length;
                                // Nothing officer-related in the selection → hide.
                                if (submittableIds.length === 0 && notReadyCount === 0) return null;
                                const allReady = submittableIds.length > 0 && notReadyCount === 0;
                                const tip = allReady
                                    ? undefined
                                    : (submittableIds.length === 0
                                        ? "Some selected rows are not ready (missing check-out or no changes made)."
                                        : `${submittableIds.length} ready · ${notReadyCount} not ready (missing check-out or no changes made). Click to submit only the ready rows.`);
                                return (
                                    <Button
                                        size="sm"
                                        disabled={submittableIds.length === 0}
                                        className={submittableIds.length === 0
                                            ? "bg-white/10 border border-white/30 text-white/60 cursor-not-allowed"
                                            : "asl-crimson-bg text-white hover:opacity-90"}
                                        onClick={submitSelected}
                                        title={tip}
                                        data-testid="att-bulk-submit"
                                    >
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        Submit {submittableIds.length} for approval
                                        {notReadyCount > 0
                                            ? <span className="ml-1 text-[10px] opacity-80">· {notReadyCount} not ready</span>
                                            : null}
                                    </Button>
                                );
                            })()}
                            {/* Iter60.p34 — Bulk Unapprove. Visible only
                                when ≥1 currently-approved live row is in
                                the selection. Dept managers hit RBAC on
                                out-of-scope rows server-side. */}
                            {canApproveDaily && approvedSelectedIds.length > 0 && (
                                <Button size="sm"
                                        variant="outline"
                                        className="bg-white/10 border-white/40 text-white hover:bg-white/20"
                                        onClick={() => setConfirmBulkUnapprove(true)}
                                        data-testid="bulk-unapprove-btn">
                                    <RotateCcw className="h-4 w-4 mr-1" /> Unapprove {approvedSelectedIds.length}
                                </Button>
                            )}
                            {canRecon && manualSelectedIds.length > 0 && (
                                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700"
                                        onClick={() => setBulkReplaceOpen(true)}
                                        data-testid="att-bulk-replace-biometric">
                                    <Zap className="h-4 w-4 mr-1" /> Replace {manualSelectedIds.length} with Biometric
                                </Button>
                            )}
                            {/* Iter60.p21 — Bulk Adjust Hours. Enabled only
                                when ≥1 selected row is a Construction row.
                                Non-eligible rows in the selection are just
                                skipped by the backend (best-effort).
                                Iter60.p23 — casual + non-casual both eligible. */}
                            {canAdjust && selectedBreakdown.live > 0 && (
                                <Button
                                    size="sm"
                                    className={
                                        cnSelectedIds.length > 0
                                            ? "bg-[color:var(--asl-crimson)] text-white hover:brightness-95"
                                            : "bg-gray-200 text-[color:var(--asl-muted)] cursor-not-allowed"
                                    }
                                    disabled={cnSelectedIds.length === 0}
                                    title={
                                        cnSelectedIds.length === 0
                                            ? "Adjustments apply only to Construction module employees"
                                            : undefined
                                    }
                                    onClick={() => setAdjustRowIds(cnSelectedIds)}
                                    data-testid="att-bulk-adjust-hours"
                                >
                                    <Zap className="h-4 w-4 mr-1" /> Adjust {cnSelectedIds.length || ""} Hours
                                </Button>
                            )}
                            {canEditAttendance && selectedBreakdown.live > 0 && (
                                <Button size="sm" className="bg-white text-[color:var(--asl-navy)] hover:bg-gray-100"
                                        onClick={() => setBulkEditOpen(true)} data-testid="att-bulk-edit">
                                    <Pencil className="h-4 w-4 mr-1" /> Edit {selectedBreakdown.live}
                                </Button>
                            )}
                            {canDeleteAttendance && selectedBreakdown.live > 0 && (
                                <Button size="sm"
                                        className="bg-[color:var(--asl-crimson)] text-white hover:brightness-95"
                                        onClick={() => setConfirmBulkDelete(true)}
                                        data-testid="att-bulk-delete">
                                    <Trash2 className="h-4 w-4 mr-1" /> Delete {selectedBreakdown.live}
                                </Button>
                            )}
                            {canDeleteAttendance && selectedBreakdown.deleted > 0 && (
                                <Button size="sm" className="bg-white text-[color:var(--asl-navy)] hover:bg-gray-100"
                                        onClick={() => setConfirmBulkRestore(true)}
                                        data-testid="att-bulk-restore">
                                    <RotateCcw className="h-4 w-4 mr-1" /> Restore {selectedBreakdown.deleted}
                                </Button>
                            )}
                            {canPurgeAttendance && selectedBreakdown.deleted > 0 && (
                                <Button size="sm"
                                        className="bg-transparent border border-white text-white hover:bg-white/10"
                                        onClick={() => setDailyPurgeOpen(true)}
                                        data-testid="att-bulk-purge">
                                    <ShieldAlert className="h-4 w-4 mr-1" /> Permanently delete {selectedBreakdown.deleted}
                                </Button>
                            )}
                            <button type="button"
                                    onClick={clearSelection}
                                    className="text-white/80 hover:text-white text-xs underline decoration-dotted"
                                    data-testid="att-bulk-clear">
                                Clear selection
                            </button>
                        </div>
                    </div>
                )}

                {/* Iter91 — Inline chips for the two HIDDEN filters that
                    survive from the Home dashboard drill-down flow. When
                    either is active, surface a dismissible chip above the
                    table so operators understand why the view is pre-
                    filtered. Clicking × clears ONLY that specific filter
                    (not the whole bar); the global "Clear filters" button
                    in the filter bar still clears everything in one shot. */}
                {(filters.has_note || (filters.employee_id && filters.employee_id !== ALL)) && (
                    <div
                        className="flex flex-wrap items-center gap-2 px-1 pb-2"
                        data-testid="att-hidden-filter-chips"
                    >
                        {filters.has_note && (
                            <button
                                type="button"
                                onClick={() => setFilters((f) => ({ ...f, has_note: false }))}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100 transition-colors"
                                title="Clear the early-departure-notes filter"
                                data-testid="att-chip-clear-has-note"
                            >
                                <StickyNote className="h-3 w-3" />
                                <span>Filtered by: early-departure notes</span>
                                <X className="h-3 w-3 ml-1" />
                            </button>
                        )}
                        {filters.employee_id && filters.employee_id !== ALL && (
                            <button
                                type="button"
                                onClick={() => setFilters((f) => ({ ...f, employee_id: ALL }))}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium bg-[color:var(--asl-navy-50)] border-[color:var(--asl-border)] asl-navy-text hover:bg-[color:var(--asl-navy-100,var(--asl-navy-50))] transition-colors"
                                title="Clear the employee drill-down filter"
                                data-testid="att-chip-clear-employee-id"
                            >
                                <span>
                                    Filtered by: employee{" "}
                                    <span className="font-semibold">
                                        {employees[filters.employee_id]?.full_name
                                            || rows[0]?.employee_name
                                            || filters.employee_id.slice(0, 8)}
                                    </span>
                                </span>
                                <X className="h-3 w-3 ml-1" />
                            </button>
                        )}
                    </div>
                )}

                {/* Iter60.p43.4 — Sticky header + inner vertical scroll
                    on the attendance grid so operators viewing a wide
                    date-range roster don't have to scroll to the very
                    bottom of the page to reach the horizontal scrollbar. */}
                <div className="asl-card asl-table-scroll" data-testid="att-table-scroll">
                    {loading ? (
                        <TableSkeleton rows={6} cols={16} testid="att-loading" />
                    ) : rows.length === 0 ? (
                        (() => {
                            const isFilt = filters.module_id !== ALL
                                || filters.status !== ALL
                                || filters.project_id !== ALL
                                || filters.employee_type !== ALL
                                || filters.worked_state !== ALL
                                || filters.has_note
                                || filters.employee_id !== ALL
                                || (filters.search && filters.search.trim());
                            return (
                                <EmptyState
                                    icon={ClipboardCheck}
                                    title={isFilt ? "Nothing matches these filters. Try widening the range or clearing a filter." : "No attendance records for the selected range"}
                                    description={isFilt ? "Adjust filters or widen the date range." : "Generate sample events for today, or add a manual entry to start."}
                                    ctaLabel={canManual && !isFilt ? "Add Manual Attendance" : undefined}
                                    onCta={canManual && !isFilt ? () => setManualOpen(true) : undefined}
                                    filtered={isFilt}
                                    onClear={clearAllFilters}
                                    testid="att-empty"
                                />
                            );
                        })()
                    ) : (
                        <table className="asl-table asl-table--zebra" data-testid="att-table">
                            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                                <tr>
                                    <th className="px-3 py-3 w-8">
                                        <Checkbox
                                            checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                                            onCheckedChange={toggleAllVisible}
                                            aria-label="Select all visible attendance rows"
                                            data-testid="attendance-select-all"
                                        />
                                    </th>
                                    <th className="text-left px-3 py-3">Employee</th>
                                    {/* Iter60.p58 — TYPE column: worker taxonomy pill sits
                                        between EMPLOYEE and MODULE ID so the fleet's
                                        casual/non-casual mix is visible at a glance. */}
                                    <th
                                        className="text-left px-3 py-3"
                                        data-testid="att-th-type"
                                    >
                                        Type
                                    </th>
                                    {/* Iter60.p40 Item C — per-employee Module ID column.
                                        Same typographic treatment as EMP-#### code cell so
                                        it reads as an "identifier chip" without stealing
                                        visual weight from the name. */}
                                    <th
                                        className="text-left px-3 py-3"
                                        data-testid="att-th-module-id"
                                    >
                                        Module ID
                                    </th>
                                    <th className="text-left px-3 py-3">Module</th>
                                    <th className="text-left px-3 py-3">Project</th>
                                    <th className="text-left px-3 py-3">Date</th>
                                    <th className="text-left px-3 py-3">In</th>
                                    <th className="text-left px-3 py-3">Out</th>
                                    <th className="text-left px-3 py-3">Worked</th>
                                    <th className="text-left px-3 py-3" data-testid="att-th-adjustments">Adjustments</th>
                                    <th className="text-left px-3 py-3" data-testid="att-th-payable">Payable</th>
                                    <th className="text-left px-3 py-3">Late</th>
                                    <th className="text-left px-3 py-3">OT</th>
                                    <th className="text-left px-3 py-3">Pay×</th>
                                    <th className="text-left px-3 py-3">Status</th>
                                    <th className="text-left px-3 py-3">Source</th>
                                    <th className="text-left px-3 py-3">Stage</th>
                                    <th className="text-right px-3 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((r) => {
                                    const emp = employees[r.employee_id];
                                    const isDeleted = !!r.deleted_at;
                                    // Iter60.p41b — Synthetic-absent rows carry `id: null`
                                    // (no backing DB doc). We build a stable client-side
                                    // key so React can diff the list and testids stay unique.
                                    const isSynthetic = !!r.synthetic;
                                    const rowKey = r.id || `syn-${r.employee_id}-${r.work_date}`;
                                    // Iter70 (Fix B) — Row body clicks no longer navigate
                                    // to the employee profile (users were losing filter
                                    // state when they clicked a data cell by accident).
                                    // Only the employee-name link inside the row now opens
                                    // the profile.
                                    const profileHref = r.employee_id ? `/m/${pathModuleCode}/staff/${r.employee_id}` : null;
                                    return (
                                        <tr key={rowKey}
                                            data-testid={`att-row-${rowKey}`}
                                            className={`border-t ${isDeleted ? "opacity-60 bg-gray-50" : ""}`}
                                            style={{ borderColor: "var(--asl-border)" }}
                                            data-deleted={isDeleted ? "1" : "0"}
                                            data-synthetic={isSynthetic ? "1" : "0"}>
                                            <td className="px-3 py-3">
                                                <Checkbox
                                                    checked={selected.has(rowKey)}
                                                    onCheckedChange={(c) => {
                                                        const s = new Set(selected);
                                                        if (c) s.add(rowKey); else s.delete(rowKey);
                                                        setSelected(s);
                                                    }}
                                                    data-testid={`att-check-${rowKey}`}
                                                    title={isSynthetic
                                                        ? "Absent row (no biometric events). Select-all includes it for banner accuracy — bulk actions like Approve / Edit / Delete skip it. Add a manual entry to make it actionable."
                                                        : undefined} />
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="font-medium asl-navy-text flex items-center gap-2 flex-wrap">
                                                    <DeletedRowDecorator deleted={isDeleted}>
                                                        {profileHref ? (
                                                            <Link
                                                                to={profileHref}
                                                                className="hover:underline focus:underline focus:outline-none rounded-sm"
                                                                title="Open profile"
                                                                data-testid={`att-name-link-${rowKey}`}
                                                            >
                                                                {emp?.full_name || r.employee_name || "—"}
                                                            </Link>
                                                        ) : (
                                                            <span>{emp?.full_name || r.employee_name || "—"}</span>
                                                        )}
                                                    </DeletedRowDecorator>
                                                    {/* Iter60.p19 cleanup — inline SourcePill removed; the
                                                        badge now lives in the dedicated Source column. */}
                                                    {r.has_test_data && (
                                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider bg-slate-500 text-white"
                                                              title="This row was generated by the Test Attendance tool"
                                                              data-testid={`att-test-badge-${r.id}`}>TEST</span>
                                                    )}
                                                    {isDeleted && <DeletedPill deletedAt={r.deleted_at} />}
                                                </div>
                                                {/* Iter60.p49 Item B — EMP-#### removed from the visible
                                                    cell. Module ID chip is rendered in its own column. */}
                                            </td>
                                            {/* Iter60.p58 — TYPE cell. Prefer the row's hydrated
                                                worker_category (list endpoint stamps it via the
                                                employee catalogue). Fallback: derive on the FE
                                                from `emp.employment_type` + `emp.worker_subtype`
                                                when the employees map is populated (Manager view).
                                                Iter60.p59 — Employment lifecycle badge stacks
                                                below the TYPE badge so back-dated views make
                                                off-boarded workers instantly visible. Suppressed
                                                for `active` so today's grid stays quiet. */}
                                            <td
                                                className="px-3 py-3 whitespace-nowrap"
                                                data-testid={`att-type-${rowKey}`}
                                            >
                                                <div className="flex flex-col gap-1">
                                                    <WorkerTypeBadge
                                                        category={
                                                            r.worker_category
                                                            || deriveWorkerCategory(
                                                                r.employment_type || emp?.employment_type,
                                                                r.worker_subtype || emp?.worker_subtype,
                                                            )
                                                        }
                                                        testid={`att-type-badge-${rowKey}`}
                                                    />
                                                    {r.employment_status && r.employment_status !== "active" && (
                                                        <EmploymentStatusBadge
                                                            status={r.employment_status}
                                                            size="xs"
                                                            testid={`att-employment-badge-${rowKey}`}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            {/* Iter60.p40 Item C — per-employee Module ID (e.g. AP003).
                                                Prefers the backend-hydrated `r.module_employee_id` (added
                                                for direct-hit consistency), falls back to the resolved
                                                employees map for backward compat.
                                                Iter60.p52 — rendered via <ModuleIdChip/> (crimson pill,
                                                same treatment as Leave Balances / Discipline / Manual
                                                Attendance) so the identifier is legible at a glance. */}
                                            <td
                                                className="px-3 py-3 whitespace-nowrap"
                                                data-testid={`att-module-id-${r.id}`}
                                            >
                                                <ModuleIdChip
                                                    value={r.module_employee_id || emp?.module_employee_id}
                                                    testid={`att-module-id-chip-${rowKey}`}
                                                />
                                            </td>
                                            <td className="px-3 py-3 text-xs">{modulesById[r.module_id]?.code}</td>
                                            <td className="px-3 py-3 text-xs" data-no-row-click>
                                                {/* Iter60.p44 — Priority order:
                                                    1. project_allocations non-empty → multi-project chip
                                                       (opens Split dialog on click).
                                                    2. Single project_id → existing entity link.
                                                    3. Neither → muted em-dash. */}
                                                {(r.project_allocations || []).some((a) => a && a.project_id && Number(a.minutes) > 0) ? (
                                                    <ProjectSplitChip
                                                        allocations={r.project_allocations}
                                                        projectsById={projectsById}
                                                        onOpen={() => canAdjust && isCnRow(r) && !r.deleted_at && setSplitRow(r)}
                                                        testid={`att-project-split-chip-${r.id}`}
                                                    />
                                                ) : r.project_id ? (
                                                    <EntityLink
                                                        to={`/m/${pathModuleCode}/attendance?project_id=${r.project_id}`}
                                                        testid={`att-project-link-${r.id}`}
                                                    >
                                                        {/* Iter60.p49.1 — Job # prefix on the single-project cell too. */}
                                                        {formatProjectLabel(projectsById[r.project_id]) || r.project_name || r.project_id.slice(0, 8)}
                                                    </EntityLink>
                                                ) : <span className="text-[color:var(--asl-muted)]">—</span>}
                                            </td>
                                            <td className="px-3 py-3 mono text-xs">{formatDate(r.work_date)}</td>
                                            <td className="px-3 py-3 mono text-xs">
                                                {fmtTime(r.first_clock_in)}
                                                <AutoClassifiedBadge
                                                    classifiedBy={r.first_clock_in_classified_by}
                                                    testid={`att-auto-classified-in-${r.id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-3 mono text-xs">
                                                {fmtTime(r.last_clock_out)}
                                                <AutoClassifiedBadge
                                                    classifiedBy={r.last_clock_out_classified_by}
                                                    testid={`att-auto-classified-out-${r.id}`}
                                                />
                                            </td>
                                            <td
                                                className="px-3 py-3 mono text-xs"
                                                title={buildWorkedTooltip(r, (activeShiftRules && activeShiftRules[0]) || null) || undefined}
                                                data-testid={`att-worked-${r.id}`}
                                            >
                                                {fmtWorked(r)}
                                            </td>
                                            {/* Iter60.p21 — Adjustments (chip stack) + Payable (bold when != worked). */}
                                            <td className="px-3 py-3 text-xs">
                                                <div className="flex items-center gap-1 flex-wrap">
                                                    <AdjustmentChips
                                                        row={r}
                                                        canRemove={canAdjust && !r.deleted_at && isCnRow(r)}
                                                        onRemove={(a) => handleRemoveAdjustment(r, a)}
                                                    />
                                                    {/* Iter60.p43 — Early-departure note chip. Rendered inline so
                                                        HR can spot annotated rows without opening a modal. Click
                                                        opens the compact dialog for edit; native `title` gives
                                                        a hover preview even when JS-driven tooltips aren't loaded. */}
                                                    {!isSynthetic && r.early_departure_note && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setEdNoteRow(r); }}
                                                            className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                                                            title={
                                                                `Early-departure note`
                                                                + `\nReason: ${(r.early_departure_reason_code || "other").toUpperCase()}`
                                                                + (r.excused_early_departure ? " · EXCUSED" : "")
                                                                + `\n\n${r.early_departure_note}`
                                                                + (r.early_departure_recorded_at
                                                                    ? `\n\nRecorded ${r.early_departure_recorded_at.slice(0, 16).replace("T", " ")}`
                                                                    : "")
                                                            }
                                                            data-testid={`att-ed-note-chip-${r.id}`}
                                                        >
                                                            <StickyNote className="h-3 w-3" />
                                                            {(r.early_departure_reason_code || "other").toUpperCase()}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td
                                                className={`px-3 py-3 mono text-xs ${
                                                    (r.payable_minutes ?? r.worked_minutes ?? 0) !== (r.worked_minutes ?? 0)
                                                        ? "font-bold asl-navy-text"
                                                        : ""
                                                }`}
                                                data-testid={`att-payable-${r.id}`}
                                            >
                                                <div className="flex flex-col gap-0.5 items-start">
                                                    <span>{fmtMinutes(r.payable_minutes ?? r.worked_minutes ?? 0)}</span>
                                                    {/* Iter63 — Schedule bonus chip. Renders below
                                                        the payable total ONLY when the row's
                                                        per-day override contributed bonus minutes. */}
                                                    {(r.schedule_bonus_minutes || 0) > 0 && (
                                                        <span
                                                            className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
                                                            style={{
                                                                background: "rgba(168,50,74,0.10)",
                                                                color: "var(--asl-crimson)",
                                                            }}
                                                            title={`${r.schedule_bonus_label || "Schedule bonus"} — added per shift rule override`}
                                                            data-testid={`att-schedule-bonus-${r.id}`}
                                                        >
                                                            + {Math.floor(r.schedule_bonus_minutes / 60)}h {r.schedule_bonus_minutes % 60}m bonus
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 mono text-xs">
                                                <div className="flex flex-col gap-0.5 items-start">
                                                    <span>{r.late_minutes ? `${r.late_minutes}m` : "—"}</span>
                                                    {/* Iter71 Fix 1 — Late deduction suppressed when payable
                                                        reaches full shift via adjustments. Raw Late minutes
                                                        stay visible for audit; this chip explains why payroll
                                                        will NOT deduct a dollar. */}
                                                    {r.late_deduction_suppressed && (
                                                        <span
                                                            className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold whitespace-nowrap"
                                                            style={{
                                                                background: "rgba(6, 95, 70, 0.10)",
                                                                color: "rgb(6, 95, 70)",
                                                                border: "1px solid rgba(6, 95, 70, 0.25)",
                                                            }}
                                                            title="Late minutes not deducted from pay — worker's payable reached full shift via adjustment."
                                                            data-testid={`att-late-suppressed-${r.id}`}
                                                        >
                                                            Not deducted — compensated
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {/* Iter82 — OT column shows Authorized vs Detected on two lines.
                                                Authorized is bold navy (pays); Detected is muted gray italic
                                                (informational, never paid). Dismissed rows show a subtle chip. */}
                                            <td className="px-3 py-3 mono text-xs" data-testid={`att-ot-cell-${r.id}`}>
                                                {(() => {
                                                    const auth = Number(r.authorized_ot_minutes || 0);
                                                    const det = Number(r.detected_ot_minutes || 0);
                                                    const fmt = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;
                                                    if (auth <= 0 && det <= 0) return "—";
                                                    return (
                                                        <div className="flex flex-col gap-0.5 leading-tight">
                                                            <span className="flex items-center gap-1">
                                                                <span
                                                                    className={auth > 0 ? "font-semibold asl-navy-text" : "text-[color:var(--asl-muted)]"}
                                                                    data-testid={`att-ot-authorized-${r.id}`}
                                                                >
                                                                    {auth > 0
                                                                        ? `Auth: ${fmt(auth)}`
                                                                        : "Auth: — (not paid)"}
                                                                </span>
                                                                {auth > 0 && (
                                                                    <Check
                                                                        className="h-3 w-3"
                                                                        style={{ color: "var(--asl-navy)" }}
                                                                    />
                                                                )}
                                                            </span>
                                                            <span
                                                                className="italic text-[10px] text-[color:var(--asl-muted)]"
                                                                data-testid={`att-ot-detected-${r.id}`}
                                                            >
                                                                {det > 0 ? `Det: ${fmt(det)}` : "Det: —"}
                                                            </span>
                                                            {r.ot_review_dismissed && (
                                                                <span
                                                                    className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold whitespace-nowrap w-fit"
                                                                    style={{ background: "rgba(107,114,128,0.10)", color: "rgb(75,85,99)", border: "1px solid rgba(107,114,128,0.22)" }}
                                                                    title={r.ot_review_dismissal_reason || "Dismissed — no OT due"}
                                                                    data-testid={`att-ot-dismissed-${r.id}`}
                                                                >
                                                                    Dismissed
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-3 py-3 mono text-xs" data-testid={`att-ot-pay-mult-${r.id}`}>
                                                {(() => {
                                                    // Iter90 — Precedence: Holiday > Weekend > Authorized OT.
                                                    //   * Holiday day → show holiday × with "H" badge.
                                                    //   * Weekend day → show weekend × with "W" badge.
                                                    //   * Workday with authorized OT → show OT × (existing behaviour).
                                                    //   * Regular workday → blank.
                                                    const auth = Number(r.authorized_ot_minutes || 0);
                                                    const authMult = r.authorized_ot_multiplier;
                                                    const dayMult = Number(r.multiplier_applied || 1);
                                                    const fmt = (m) => `${Number(m).toFixed(m % 1 === 0 ? 1 : 2)}×`;
                                                    if (r.is_holiday && dayMult > 1) {
                                                        return (
                                                            <span data-testid={`att-pay-mult-holiday-${r.id}`}>
                                                                {fmt(dayMult)}{" "}
                                                                <span className="text-[9px] font-semibold text-cyan-700">H</span>
                                                            </span>
                                                        );
                                                    }
                                                    if (r.is_weekend && dayMult > 1) {
                                                        return (
                                                            <span data-testid={`att-pay-mult-weekend-${r.id}`}>
                                                                {fmt(dayMult)}{" "}
                                                                <span className="text-[9px] font-semibold text-amber-700">W</span>
                                                            </span>
                                                        );
                                                    }
                                                    if (auth > 0 && authMult) {
                                                        return fmt(authMult);
                                                    }
                                                    return "";
                                                })()}
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(r.status)}`}>
                                                        {STATUS_LABEL[r.status] || r.status}
                                                    </span>
                                                    {/* Iter60.p47 Item B — Amber "ON LEAVE" / "PERMISSION" chip.
                                                        Rendered for any row (all modules) whose (employee_id,
                                                        work_date) has an approved leave. Full-day → "ON LEAVE ·
                                                        Type"; permission_hours → "PERMISSION · HH:MM–HH:MM". */}
                                                    {!isSynthetic && r.active_leave && (
                                                        <LeaveChip active={r.active_leave} rowId={r.id} />
                                                    )}
                                                    {/* Iter60.p60 Part B — Public-holiday name chip.
                                                        Visible on every row (real or synthetic) whose date is a
                                                        public holiday for the row's module. Payroll already
                                                        applies the shift-rule holiday_rate_multiplier so this is
                                                        purely a visibility hint for HR. */}
                                                    {r.public_holiday_name && (
                                                        <span
                                                            className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5"
                                                            style={{
                                                                background: "rgba(168, 50, 74, 0.10)",
                                                                color: "var(--asl-crimson, #A8324A)",
                                                                border: "1px solid rgba(168, 50, 74, 0.22)",
                                                            }}
                                                            title={`Public holiday — pay uplift applies per the module's shift rule.`}
                                                            data-testid={`att-holiday-chip-${r.id || `${r.employee_id}-${r.work_date}`}`}
                                                        >
                                                            Holiday: {r.public_holiday_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3">
                                                {/* Iter60.p19 — Source column: Manual / Biometric / Mixed pill */}
                                                <SourcePill row={r} />
                                            </td>
                                            <td className="px-3 py-3">
                                                {/* Iter60.p54 — Three-stage rendering. `stage` is
                                                    authoritative; legacy rows fall back to the boolean.
                                                    Iter60.p54.1 — When the row transitioned through
                                                    `submitted` (i.e. `submitted_by_officer_id` is set),
                                                    render a small neutral `Officer` provenance badge
                                                    beside the stage chip so the Manager can tell at a
                                                    glance that this row originated from an Officer
                                                    submission — even after it's `approved`. */}
                                                <div className="inline-flex items-center gap-1.5 flex-wrap">
                                                    {(() => {
                                                        const st = r.stage || (r.approved ? "approved" : "pending");
                                                        if (st === "approved") {
                                                            return <span className="text-emerald-700 text-xs font-semibold" data-testid={`att-stage-${r.id}`}>✓ Approved</span>;
                                                        }
                                                        if (st === "submitted") {
                                                            return (
                                                                <span
                                                                    className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5"
                                                                    style={{
                                                                        background: "rgba(168, 50, 74, 0.10)",
                                                                        color: "var(--asl-crimson)",
                                                                        border: "1px solid rgba(168, 50, 74, 0.22)",
                                                                    }}
                                                                    title={r.submitted_by_officer_email
                                                                        ? `Submitted by ${r.submitted_by_officer_email}`
                                                                        : "Submitted for approval"}
                                                                    data-testid={`att-stage-${r.id}`}
                                                                >
                                                                    Submitted
                                                                </span>
                                                            );
                                                        }
                                                        return <span className="text-[color:var(--asl-muted)] text-xs" data-testid={`att-stage-${r.id}`}>Pending</span>;
                                                    })()}
                                                    {/* Iter60.p54.1 — Officer provenance badge.
                                                        Visible on any row where the stage ever
                                                        transitioned through `submitted` (submitted
                                                        or approved-via-officer). Neutral navy tone
                                                        — crimson is reserved for the manager
                                                        counter chip. Tooltip carries submit +
                                                        approve timestamps and emails when known. */}
                                                    {r.submitted_by_officer_id && (() => {
                                                        const parts = [];
                                                        if (r.submitted_by_officer_email && r.submitted_by_officer_at) {
                                                            parts.push(`Submitted by ${r.submitted_by_officer_email} on ${formatDateTime(r.submitted_by_officer_at)}`);
                                                        } else if (r.submitted_by_officer_email) {
                                                            parts.push(`Submitted by ${r.submitted_by_officer_email}`);
                                                        } else if (r.submitted_by_officer_at) {
                                                            parts.push(`Submitted on ${formatDateTime(r.submitted_by_officer_at)}`);
                                                        }
                                                        if (r.approved && r.approved_at) {
                                                            const who = r.approved_by_email ? `by ${r.approved_by_email} ` : "";
                                                            parts.push(`Approved ${who}on ${formatDateTime(r.approved_at)}`);
                                                        }
                                                        const tip = parts.length ? parts.join("\n") : "Submitted by Officer";
                                                        return (
                                                            <span
                                                                className="inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5"
                                                                style={{
                                                                    background: "var(--asl-navy-50)",
                                                                    color: "var(--asl-navy)",
                                                                    border: "1px solid var(--asl-border)",
                                                                }}
                                                                title={tip}
                                                                data-testid={`att-officer-badge-${r.id}`}
                                                                aria-label="Submitted by Attendance Officer"
                                                            >
                                                                Officer
                                                            </span>
                                                        );
                                                    })()}
                                                    {/* Iter60.p54.2 — Manager "View changes" popover.
                                                        Rendered next to the Officer provenance badge on
                                                        any row that has pending_changes recorded
                                                        (typically the Submitted stage). Officer sees
                                                        this too — no harm, keeps the DOM consistent
                                                        across roles. Popover is inert on rows with an
                                                        empty pending_changes array. */}
                                                    {r.submitted_by_officer_id && (r.pending_changes || []).length > 0 && (
                                                        <OfficerChangeDiffPopover row={r} />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-right">
                                                <div className="inline-flex items-center gap-1 justify-end">
                                                    {/* Iter60.p54 — Officer submit button. Only
                                                        rendered on rows the Officer touched AND still
                                                        in `pending`. Tooltip explains disabled state
                                                        on synthetic / bio rows. */}
                                                    {isOfficer && !isSynthetic && officerCanSubmit(r) && (
                                                        <Button
                                                            size="sm"
                                                            onClick={(e) => { e.stopPropagation(); submitOne(r); }}
                                                            className="asl-crimson-bg text-white hover:opacity-90"
                                                            data-testid={`att-submit-${r.id}`}
                                                        >
                                                            Submit
                                                        </Button>
                                                    )}
                                                    {!isSynthetic && !r.approved && canApprove && (
                                                        <Button size="sm" variant="outline" onClick={() => approveOne(r)}
                                                            data-testid={`att-approve-${r.id}`}>
                                                            Approve
                                                        </Button>
                                                    )}
                                                    {isSynthetic ? (
                                                        <span
                                                            className="text-[10px] text-[color:var(--asl-muted)] italic px-1"
                                                            title="No biometric events — add a manual entry to record this day"
                                                            data-testid={`att-synthetic-hint-${r.employee_id}`}
                                                        >
                                                            No events
                                                        </span>
                                                    ) : (
                                                        <ReclassifyMenu
                                                        row={r}
                                                        isAdmin={isAdmin}
                                                        canEdit={canEditAttendance && !r.deleted_at}
                                                        canRecon={canRecon}
                                                        canAdjust={canAdjust}
                                                        canApprove={canApproveDaily}
                                                        canSplitProjects={canEditAttendance}
                                                        canAddOT={canAddOT && !r.deleted_at && !r.synthetic}
                                                        adjustEligible={isCnRow(r)}
                                                        moduleCode={pathModuleCode}
                                                        onEdit={setEditRow}
                                                        onReplaceBiometric={(row) => setReplaceRow(row)}
                                                        onDeleteManual={(row) => setDeleteRow(row)}
                                                        onAdjustHours={(row) => setAdjustRowIds([row.id])}
                                                        onUnapprove={unapproveOne}
                                                        onAddOT={(row) => setOtSingleRow({
                                                            ...row,
                                                            employee_name: emp?.full_name || row.employee_name,
                                                        })}
                                                        // Iter88 — open Remove-OT dialog on the row.
                                                        onRemoveOT={(row) => setOtRemoveRow({
                                                            ...row,
                                                            employee_name: emp?.full_name || row.employee_name,
                                                        })}
                                                        onSplitProjects={(row) => setSplitRow({
                                                            ...row,
                                                            // Iter60.p40 — enrich the row with the resolved employee
                                                            // record so the compact dialog can render the name (the
                                                            // attendance row itself doesn't carry `full_name`).
                                                            employee_name: emp?.full_name || row.employee_name,
                                                            employee_code: emp?.employee_code || row.employee_code,
                                                        })}
                                                        // Iter60.p43 — open the compact early-departure note dialog.
                                                        onEarlyDepartureNote={(row) => setEdNoteRow({
                                                            ...row,
                                                            employee_name: emp?.full_name || row.employee_name,
                                                            employee_code: emp?.employee_code || row.employee_code,
                                                        })}
                                                        onDone={load}
                                                    />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                <EventsSubTab filters={filters} />
            </section>

            {/* Generate Test Attendance — confirmation dialog (admin only) */}
            <Dialog open={generateOpen} onOpenChange={(o) => { if (!o) { setGenerateOpen(false); setGenPhrase(""); } }}>
                <DialogContent className="max-w-md" data-testid="att-generate-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <TriangleAlert className="h-5 w-5" /> Generate SIMULATED attendance
                        </DialogTitle>
                        <DialogDescription>
                            This inserts <b>fake, randomly-generated</b> attendance events for every active
                            employee in the currently selected module, tagged <span className="mono">source: test_generated</span>.
                            Real biometric scans (ZKTeco / Hikvision) are preserved.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
                        <div className="font-semibold">Intended for testing only. Never run in production.</div>
                        <div>Module: <b>{filters.module_id === ALL ? "All modules" : (modules.find((m) => m.id === filters.module_id)?.name || "—")}</b></div>
                        <div>Date: <b>{todayIso()}</b></div>
                    </div>
                    <div>
                        <Label className="text-xs">Type <span className="mono">GENERATE</span> to confirm</Label>
                        <Input value={genPhrase} onChange={(e) => setGenPhrase(e.target.value)}
                               placeholder="GENERATE" data-testid="att-generate-phrase" autoFocus />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setGenerateOpen(false); setGenPhrase(""); }} disabled={busy}
                                data-testid="att-generate-cancel">Cancel</Button>
                        <Button className="bg-amber-500 hover:bg-amber-600 text-white"
                                disabled={busy || genPhrase !== "GENERATE"}
                                onClick={generateToday} data-testid="att-generate-confirm">
                            {busy ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <TriangleAlert className="h-4 w-4 mr-1" />}
                            Insert test data
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Purge Test Data — confirmation dialog (admin only) */}
            <Dialog open={purgeOpen} onOpenChange={(o) => { if (!o) { setPurgeOpen(false); setPurgePhrase(""); } }}>
                <DialogContent className="max-w-md" data-testid="att-purge-dialog">
                    <DialogHeader>
                        <DialogTitle className="asl-crimson-text flex items-center gap-2">
                            <Trash2 className="h-5 w-5" /> Purge test-generated attendance
                        </DialogTitle>
                        <DialogDescription>
                            Deletes every event with <span className="mono">source: test_generated</span> in the
                            current filter window and resets the affected daily summaries.
                            Real biometric events are preserved.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border p-3 text-xs space-y-1"
                         style={{ borderColor: "var(--asl-crimson)", background: "var(--asl-crimson-50, #fdecef)" }}>
                        <div>Module: <b>{filters.module_id === ALL ? "All modules" : (modules.find((m) => m.id === filters.module_id)?.name || "—")}</b></div>
                        <div>Date range: <b>{filters.date_from} → {filters.date_to}</b></div>
                    </div>
                    <div>
                        <Label className="text-xs">Type <span className="mono">DELETE TEST DATA</span> to confirm</Label>
                        <Input value={purgePhrase} onChange={(e) => setPurgePhrase(e.target.value)}
                               placeholder="DELETE TEST DATA" data-testid="att-purge-phrase" autoFocus />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setPurgeOpen(false); setPurgePhrase(""); }} disabled={busy}
                                data-testid="att-purge-cancel">Cancel</Button>
                        <Button className="asl-crimson-bg text-white"
                                disabled={busy || purgePhrase !== "DELETE TEST DATA"}
                                onClick={purgeTestData} data-testid="att-purge-confirm">
                            {busy ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                            Purge
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}


function EventsSubTab({ filters }) {
    const navigate = useNavigate();
    const { moduleCode: pathModuleCode } = useParams();
    const [events, setEvents] = useState([]);
    const [showDeleted, setShowDeleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    // Iter60.p16 — link-unmatched machinery.
    const [employees, setEmployees] = useState([]);
    const [devicesById, setDevicesById] = useState({});
    const [mapping, setMapping] = useState(null);        // { ev, dev } | null
    const [bulkOpen, setBulkOpen] = useState(false);
    const [summary, setSummary] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const params = {
                date_from: filters.date_from, date_to: filters.date_to, page_size: 200,
            };
            if (filters.module_id && filters.module_id !== ALL) params.module_id = filters.module_id;
            if (showDeleted) params.include_deleted = true;
            const { data } = await api.get("/attendance/events", { params });
            setEvents(Array.isArray(data) ? data : (data?.items || []));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    // Iter60.p16 — pull employees + devices + unmatched summary in parallel so
    // the Link buttons and Bulk Link dialog have everything they need offline.
    // Uses `Promise.allSettled` so a failure on ONE call (e.g. `/employees`
    // 422 on a stale param) doesn't hide the "Link unmatched (N)" button.
    const loadLinkAssets = async () => {
        const empParams = { page_size: 200 };
        if (filters.module_id && filters.module_id !== ALL) empParams.module_id = filters.module_id;
        const sumParams = filters.module_id && filters.module_id !== ALL
            ? { module_id: filters.module_id, date_from: filters.date_from, date_to: filters.date_to }
            : { date_from: filters.date_from, date_to: filters.date_to };
        const [empRes, devRes, sumRes] = await Promise.allSettled([
            api.get("/employees", { params: empParams }),
            api.get("/devices"),
            api.get("/attendance/events/unmatched-summary", { params: sumParams }),
        ]);
        if (empRes.status === "fulfilled") {
            setEmployees(empRes.value.data?.items || []);
        } else {
            // eslint-disable-next-line no-console
            console.warn("Iter60.p16: /employees failed", empRes.reason);
        }
        if (devRes.status === "fulfilled") {
            const devMap = {};
            (devRes.value.data || []).forEach((d) => { devMap[d.id] = d; });
            setDevicesById(devMap);
        } else {
            // eslint-disable-next-line no-console
            console.warn("Iter60.p16: /devices failed", devRes.reason);
        }
        if (sumRes.status === "fulfilled") {
            setSummary(sumRes.value.data || []);
        } else {
            // eslint-disable-next-line no-console
            console.warn("Iter60.p16: /unmatched-summary failed", sumRes.reason);
        }
    };

    useEffect(() => {
        if (open) { load(); loadLinkAssets(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, filters.module_id, filters.date_from, filters.date_to, showDeleted]);

    const unmatchedCount = summary.reduce((n, g) => n + (g.event_count || 0), 0);

    return (
        <div className="asl-card overflow-hidden" data-testid="attendance-events-section">
            <button
                type="button"
                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50"
                onClick={() => setOpen((v) => !v)}
                data-testid="events-tab-toggle"
            >
                <span className="text-sm font-semibold asl-navy-text">Events {open ? "▾" : "▸"}</span>
                <span className="text-xs text-[color:var(--asl-muted)]">{open ? `${events.length} loaded` : "click to expand"}</span>
            </button>
            {open && (
                <div className="p-4 space-y-3 border-t" style={{ borderColor: "var(--asl-border)" }}>
                    <div className="flex justify-between items-center">
                        {summary.length > 0 ? (
                            <Button
                                variant="outline" size="sm"
                                className="border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900"
                                onClick={() => setBulkOpen(true)}
                                data-testid="events-bulk-link-btn"
                            >
                                <Link2 className="h-4 w-4 mr-1" />
                                Link unmatched ({unmatchedCount})
                            </Button>
                        ) : <span />}
                        <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="events-show-deleted" />
                    </div>
                    {loading ? <div className="text-sm text-[color:var(--asl-muted)] py-4">Loading events…</div>
                    : events.length === 0 ? <div className="text-sm text-[color:var(--asl-muted)] py-4">No events for this range.</div>
                    : <table className="asl-table asl-table--zebra" data-testid="events-table">
                        <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                            style={{ background: "var(--asl-navy-50)" }}>
                            <tr>
                                <th className="text-left px-3 py-2">When</th>
                                <th className="text-left px-3 py-2">Employee</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Source</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-right px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((e) => {
                                // Iter60.p16 — an event is "unmatched" when no employee resolved
                                // AND the ingest kept the device-side ID (`raw_biometric_user_id`).
                                // These rows get an inline hint + Link button; row-click nav is
                                // disabled (there's no staff profile to open).
                                const isUnmatched = !e.employee_id
                                    && !!(e.raw_biometric_user_id || "").trim()
                                    && e.exception_reason === "unmapped_biometric_user_id";
                                return (
                                    <tr key={e.id}
                                        data-testid={`event-row-${e.id}`}
                                        className="border-t"
                                        style={{ borderColor: "var(--asl-border)" }}>
                                        <td className="px-3 py-2 mono text-xs">
                                            <DeletedRowDecorator deleted={!!e.deleted_at}>{formatDateTime(e.event_time)}</DeletedRowDecorator>
                                            {e.deleted_at && <DeletedPill deletedAt={e.deleted_at} />}
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {isUnmatched ? (
                                                <span
                                                    className="italic text-amber-800"
                                                    data-testid={`event-unmatched-${e.id}`}
                                                    title="No employee is bound to this device ID. Click Link to bind."
                                                >
                                                    &lt;unmatched: #{e.raw_biometric_user_id}&gt;
                                                </span>
                                            ) : e.employee_id ? (
                                                // Iter70 (Fix B) — Only the ID text is clickable now.
                                                // Row body clicks stay inert so users don't lose their
                                                // filters by mis-clicking a data cell.
                                                <Link
                                                    to={`/m/${pathModuleCode}/staff/${e.employee_id}`}
                                                    className="mono hover:underline focus:underline focus:outline-none"
                                                    title="Open profile"
                                                    data-testid={`event-name-link-${e.id}`}
                                                >
                                                    {e.employee_id.slice(0, 8)}
                                                </Link>
                                            ) : (
                                                <span className="mono">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs">{e.event_type}</td>
                                        <td className="px-3 py-2 text-xs">{e.source}</td>
                                        <td className="px-3 py-2 text-xs">{e.status}</td>
                                        <td className="px-3 py-2 text-right">
                                            {isUnmatched ? (
                                                <Button
                                                    size="sm" variant="outline"
                                                    className="border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900"
                                                    onClick={(clickEv) => {
                                                        clickEv.stopPropagation();
                                                        setMapping({ ev: e, dev: devicesById[e.device_id] || null });
                                                    }}
                                                    data-testid={`event-link-btn-${e.id}`}
                                                >
                                                    <Link2 className="h-3 w-3 mr-1" /> Link…
                                                </Button>
                                            ) : (
                                                <RowActions
                                                    entity="attendance"
                                                    endpoint="/attendance/events"
                                                    id={e.id}
                                                    label={e.id.slice(0, 8)}
                                                    deleted={!!e.deleted_at}
                                                    moduleId={e.module_id}
                                                    onChange={load}
                                                    testid={`event-actions-${e.id}`}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>}
                </div>
            )}
            <MapBiometricDialog
                state={mapping}
                onClose={() => setMapping(null)}
                onMapped={() => { setMapping(null); load(); loadLinkAssets(); }}
                items={events}
                employees={employees}
            />
            <BulkLinkDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onDone={() => { setBulkOpen(false); load(); loadLinkAssets(); }}
                summary={summary}
                employees={employees}
            />
        </div>
    );
}


/**
 * Iter60.p16 — Bulk link dialog.
 *
 * Renders one row per unique (device, raw_biometric_user_id) group and
 * lets the user pick an employee from a per-row dropdown. On confirm,
 * fires one `POST /attendance/events/{sample_event_id}/map-biometric`
 * per row that has an employee selected. The backend already backfills
 * every past event with the same raw+device pair, so a single call per
 * group is sufficient.
 *
 * Per-row status is shown inline after submit so the user can see
 * successes vs failures without reading a toast stream.
 */
function BulkLinkDialog({ open, onClose, onDone, summary, employees }) {
    const [picks, setPicks] = useState({});     // { `${device_id}|${raw}`: employee_id }
    const [rowStatus, setRowStatus] = useState({}); // { key: "ok" | "fail" | "skipped" }
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setPicks({}); setRowStatus({}); }
    }, [open]);

    const keyFor = (g) => `${g.device_id}|${g.raw_biometric_user_id}`;

    const submit = async () => {
        const targets = summary
            .map((g) => ({ g, empId: picks[keyFor(g)] }))
            .filter((x) => x.empId);
        if (targets.length === 0) {
            toast.error("Pick at least one employee to link.");
            return;
        }
        setBusy(true);
        const nextStatus = {};
        let okCount = 0;
        let failCount = 0;
        for (const { g, empId } of targets) {
            try {
                await api.post(`/attendance/events/${g.sample_event_id}/map-biometric`, {
                    employee_id: empId,
                });
                nextStatus[keyFor(g)] = "ok";
                okCount += 1;
            } catch (e) {
                nextStatus[keyFor(g)] = "fail";
                failCount += 1;
                // eslint-disable-next-line no-console
                console.warn("bulk link failed for", g, e);
            }
        }
        setRowStatus(nextStatus);
        setBusy(false);
        if (okCount > 0 && failCount === 0) {
            toast.success(`Linked ${okCount} device ID${okCount === 1 ? "" : "s"} — past events reclassified.`);
            onDone();
        } else if (okCount > 0) {
            toast.warning(`Linked ${okCount}, ${failCount} failed. See row markers.`);
        } else {
            toast.error(`All ${failCount} link attempts failed.`);
        }
    };

    if (!open) return null;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl" data-testid="events-bulk-link-dialog">
                <DialogHeader>
                    <DialogTitle>Link unmatched device IDs to employees</DialogTitle>
                    <DialogDescription className="text-xs">
                        Each row below is a unique device-side ID with no ASL employee bound to it.
                        Pick an employee per row. On confirm, we&apos;ll link the ID + reclassify
                        every past event that used it. Rows you leave blank stay unlinked.
                    </DialogDescription>
                </DialogHeader>
                {summary.length === 0 ? (
                    <div className="p-4 text-sm text-[color:var(--asl-muted)]">
                        Nothing to link — every event has been resolved.
                    </div>
                ) : (
                    <div className="max-h-[60vh] overflow-y-auto border rounded-md"
                         style={{ borderColor: "var(--asl-border)" }}>
                        <table className="asl-table w-full">
                            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{ background: "var(--asl-navy-50)" }}>
                                <tr>
                                    <th className="text-left px-3 py-2">Device</th>
                                    <th className="text-left px-3 py-2">Raw ID</th>
                                    <th className="text-left px-3 py-2">Events</th>
                                    <th className="text-left px-3 py-2">First → Last</th>
                                    <th className="text-left px-3 py-2">Assign employee</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.map((g) => {
                                    const k = keyFor(g);
                                    const st = rowStatus[k];
                                    return (
                                        <tr key={k} className="border-t" style={{ borderColor: "var(--asl-border)" }}
                                            data-testid={`bulk-link-row-${g.raw_biometric_user_id}`}>
                                            <td className="px-3 py-2 text-xs">
                                                <div className="font-medium asl-navy-text">{g.device_name || "—"}</div>
                                                <div className="mono text-[10px] text-[color:var(--asl-muted)]">{g.device_serial || "—"}</div>
                                            </td>
                                            <td className="px-3 py-2 mono text-xs asl-crimson-text">#{g.raw_biometric_user_id}</td>
                                            <td className="px-3 py-2 text-xs font-semibold">{g.event_count}</td>
                                            <td className="px-3 py-2 mono text-[10px] text-[color:var(--asl-muted)]">
                                                <div>{formatDateTime(g.first_seen)}</div>
                                                <div>{formatDateTime(g.last_seen)}</div>
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                <Select
                                                    value={picks[k] || ""}
                                                    onValueChange={(v) => setPicks((p) => ({ ...p, [k]: v }))}
                                                    disabled={busy || st === "ok"}
                                                >
                                                    <SelectTrigger
                                                        className="h-8 text-xs w-full"
                                                        data-testid={`bulk-link-pick-${g.raw_biometric_user_id}`}
                                                    >
                                                        <SelectValue placeholder="— pick employee —" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {employees
                                                            .filter((e) => e.status === "active" && (!g.device_module_id || e.module_id === g.device_module_id))
                                                            .slice(0, 200)
                                                            .map((e) => (
                                                                <SelectItem key={e.id} value={e.id}>
                                                                    {/* Iter60.p49 Item B — Show Module ID, not EMP-####. */}
                                                                    {e.module_employee_id ? (
                                                                        <><span className="mono text-[10px]">{e.module_employee_id}</span> · {e.full_name}</>
                                                                    ) : (
                                                                        e.full_name
                                                                    )}
                                                                </SelectItem>
                                                            ))}
                                                    </SelectContent>
                                                </Select>
                                                {/* Iter60.p16 — advisory hint. NOT pre-selected — the
                                                    on-device→system correlation can be arbitrary. Admin
                                                    must click to confirm. */}
                                                {g.suggested_employee && !picks[k] && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setPicks((p) => ({ ...p, [k]: g.suggested_employee.id }))}
                                                        disabled={busy || st === "ok"}
                                                        className="mt-1 text-[10px] text-[color:var(--asl-muted)] italic hover:underline hover:text-slate-700 text-left"
                                                        data-testid={`bulk-link-hint-${g.raw_biometric_user_id}`}
                                                        title="Click to accept this suggestion — verify the person before confirming"
                                                    >
                                                        Suggested: {g.suggested_employee.full_name}
                                                        {g.suggested_employee.module_employee_id ? ` (${g.suggested_employee.module_employee_id}` : " ("}
                                                        {g.suggested_employee.delta != null ? `, Δ${g.suggested_employee.delta}` : ""})
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {st === "ok" && <span className="text-emerald-700 font-semibold">✓ Linked</span>}
                                                {st === "fail" && <span className="text-red-700 font-semibold">✕ Failed</span>}
                                                {!st && <span className="text-[color:var(--asl-muted)] italic">Pending</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
                    <Button
                        onClick={submit}
                        className="asl-navy-bg text-white"
                        disabled={busy || summary.length === 0}
                        data-testid="events-bulk-link-submit"
                    >
                        {busy ? "Linking…" : `Link ${Object.values(picks).filter(Boolean).length} selected`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// Admin-only kebab menu on each Attendance row.
// Iter48 — now also exposes "Edit row" for anyone with attendance.write_manual
// (biometric-source + approved-revert logic is enforced server-side).
function ReclassifyMenu({ row, isAdmin, canEdit, canRecon, canAdjust, canApprove, canSplitProjects, canAddOT, adjustEligible, moduleCode, onEdit, onReplaceBiometric, onDeleteManual, onAdjustHours, onUnapprove, onSplitProjects, onEarlyDepartureNote, onAddOT, onRemoveOT, onDone }) {
    const [busy, setBusy] = useState(false);
    const showReclassify = isAdmin && (row.first_clock_in || row.last_clock_out);
    // Iter60.p19 — Reconcile items visible only for Manual rows to a
    // permitted role.
    const isManual = isManualRow(row);
    const showReconcile = canRecon && isManual && !row.deleted_at;
    // Iter60.p21 — Adjust Hours item is only shown for Construction
    // rows (adjustEligible) to a permitted role. Iter60.p23 — casual +
    // non-casual both allowed.
    const showAdjust = canAdjust && adjustEligible && !row.deleted_at;
    // Iter60.p34 — single-row Unapprove item is only shown for currently
    // approved live rows to a permitted role.
    const showUnapprove = canApprove && row.approved && !row.deleted_at;
    // Iter60.p35 Part A — Split by projects is IC + CN only (never AP)
    // and requires edit permission on the row. Same underlying dialog
    // as Edit Attendance — this shortcut opens it and scrolls to the
    // Project Split section.
    const showSplitProjects = canSplitProjects && !row.deleted_at
        && (moduleCode || "").toUpperCase() !== "AP";
    // Iter60.p43 — Early-departure note is available on any real row
    // the user can edit. Hidden on synthetic AUTO rows (they carry no
    // id server-side and the endpoint refuses them anyway).
    const showEarlyDepartureNote = canEdit && !row.synthetic && !!row.id && !row.deleted_at;
    // Iter61 — Authorized OT entry. Only for real (non-synthetic) rows.
    const showAddOT = !!canAddOT && !row.synthetic && !!row.id && !row.deleted_at;
    // Iter88 — Remove-OT item. Same RBAC + row-eligibility as Add OT,
    // and additionally requires the row to carry at least one live
    // (non-superseded) authorized OT entry OR a non-zero
    // `authorized_ot_minutes` so legacy rows still surface the action.
    const _liveOtEntries = Array.isArray(row.ot_entries)
        ? row.ot_entries.filter((e) => !e.superseded_at)
        : [];
    const showRemoveOT = showAddOT && (
        (Number(row.authorized_ot_minutes) || 0) > 0
        || _liveOtEntries.length > 0
    );
    // Hide entirely if no action is available for this user/row.
    if (!showReclassify && !canEdit && !showReconcile && !showAdjust && !showUnapprove && !showSplitProjects && !showEarlyDepartureNote && !showAddOT) return null;

    const fetchLast = async () => {
        const { data } = await api.get("/attendance/events", {
            params: {
                employee_id: row.employee_id,
                date_from: row.work_date,
                date_to: row.work_date,
                page_size: 200,
            },
        });
        const evs = Array.isArray(data) ? data : (data?.items || []);
        // Backend already sorts by event_time DESC; also filter out any soft-deleted rows.
        return evs.find((e) => !e.deleted_at && e.status === "accepted") || null;
    };

    const doReclassify = async (targetType) => {
        setBusy(true);
        try {
            const last = await fetchLast();
            if (!last) {
                toast.error("No accepted events found for this employee on this date.");
                return;
            }
            if (last.event_type === targetType) {
                toast.info(`Last event is already ${targetType.replace("_", " ")}.`);
                return;
            }
            await api.post(`/attendance/events/${last.id}/reclassify`, null, {
                params: { new_type: targetType },
            });
            const when = new Date(last.event_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            const from = last.event_type.replace("_", " ");
            const to = targetType.replace("_", " ");
            toast.success(`Reclassified ${when} · ${from} → ${to}`);
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[color:var(--asl-muted)] hover:bg-gray-100 disabled:opacity-40"
                    disabled={busy}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Row actions"
                    data-testid={`att-row-menu-${row.id}`}
                >
                    <MoreVertical className="h-4 w-4" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                {canEdit && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Edit row
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onEdit && onEdit(row); }}
                            data-testid={`att-edit-row-${row.id}`}
                        >
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit attendance
                        </DropdownMenuItem>
                        {(showReclassify || showReconcile) && <DropdownMenuSeparator />}
                    </>
                )}
                {showReconcile && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Reconcile manual entry
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onReplaceBiometric && onReplaceBiometric(row); }}
                            data-testid={`att-replace-biometric-${row.id}`}
                        >
                            <Zap className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                            Replace with Biometric…
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onDeleteManual && onDeleteManual(row); }}
                            className="text-[color:var(--asl-crimson)] focus:text-[color:var(--asl-crimson)]"
                            data-testid={`att-delete-manual-${row.id}`}
                        >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            Delete Manual Entry…
                        </DropdownMenuItem>
                        {(showReclassify || showAdjust) && <DropdownMenuSeparator />}
                    </>
                )}
                {showAdjust && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Hours adjustment
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onAdjustHours && onAdjustHours(row); }}
                            data-testid={`att-adjust-hours-${row.id}`}
                        >
                            <Zap className="h-3.5 w-3.5 mr-2 text-[color:var(--asl-crimson)]" />
                            Adjust Hours…
                        </DropdownMenuItem>
                        {(showUnapprove || showReclassify) && <DropdownMenuSeparator />}
                    </>
                )}
                {showUnapprove && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Approval
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onUnapprove && onUnapprove(row); }}
                            data-testid={`att-unapprove-${row.id}`}
                        >
                            <RotateCcw className="h-3.5 w-3.5 mr-2 text-[color:var(--asl-muted)]" />
                            Unapprove
                        </DropdownMenuItem>
                        {(showSplitProjects || showReclassify) && <DropdownMenuSeparator />}
                    </>
                )}
                {showSplitProjects && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Project split
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={() => onSplitProjects && onSplitProjects(row)}
                            data-testid={`att-split-projects-${row.id}`}
                        >
                            <Zap className="h-3.5 w-3.5 mr-2 text-[color:var(--asl-navy)]" />
                            Split by projects…
                        </DropdownMenuItem>
                        {(showEarlyDepartureNote || showReclassify) && <DropdownMenuSeparator />}
                    </>
                )}
                {/* Iter60.p43 — Early-departure note (opens compact dialog). */}
                {showEarlyDepartureNote && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Early departure
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onEarlyDepartureNote && onEarlyDepartureNote(row); }}
                            data-testid={`att-early-departure-note-${row.id}`}
                        >
                            <StickyNote className="h-3.5 w-3.5 mr-2 text-amber-600" />
                            {row.early_departure_note ? "Edit early-departure note…" : "Add early-departure note…"}
                        </DropdownMenuItem>
                        {(showAddOT || showReclassify) && <DropdownMenuSeparator />}
                    </>
                )}
                {/* Iter61 — Authorized OT (opens the OT entry dialog). */}
                {showAddOT && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Overtime
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); onAddOT && onAddOT(row); }}
                            data-testid={`att-add-ot-${row.id}`}
                        >
                            <Zap className="h-3.5 w-3.5 mr-2 text-[color:var(--asl-crimson)]" />
                            Add OT hours…
                        </DropdownMenuItem>
                        {/* Iter88 — Remove-OT sibling. Only shown when
                            the row has at least one authorized OT entry
                            (or legacy `authorized_ot_minutes > 0`). */}
                        {showRemoveOT && (
                            <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); onRemoveOT && onRemoveOT(row); }}
                                className="text-[color:var(--asl-crimson)] focus:text-[color:var(--asl-crimson)]"
                                data-testid={`att-remove-ot-${row.id}`}
                            >
                                <Trash2 className="h-3.5 w-3.5 mr-2" />
                                Remove OT hours…
                            </DropdownMenuItem>
                        )}
                        {showReclassify && <DropdownMenuSeparator />}
                    </>
                )}
                {showReclassify && (
                    <>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Reclassify last event
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); doReclassify("clock_in"); }}
                            data-testid={`att-reclassify-in-${row.id}`}
                        >
                            → Clock In
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); doReclassify("clock_out"); }}
                            data-testid={`att-reclassify-out-${row.id}`}
                        >
                            → Clock Out
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}


function Stat({ label, value, accent }) {
    const cls = accent === "rose" ? "text-rose-700" : "asl-navy-text";
    return (
        <div className="asl-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className={`mt-1 text-2xl font-extrabold ${cls}`}>{value}</div>
        </div>
    );
}

function FieldDate({ label, value, onChange, testid }) {
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <DateInput value={value} onChange={onChange} className="w-40" data-testid={testid} />
        </div>
    );
}

// Iter60.p47 Item B — Amber "ON LEAVE" / "PERMISSION" chip.
//
// Shown next to the status pill on a Daily Attendance row when the
// row's (employee_id, work_date) is covered by an approved leave
// request. Chip label + tooltip vary by request_kind:
//   full_day          → "ON LEAVE · <Type>" (title shows date range)
//   permission_hours  → "PERMISSION · HH:MM–HH:MM"
function LeaveChip({ active, rowId }) {
    if (!active) return null;
    const kind = active.kind === "permission_hours" ? "permission_hours" : "full_day";
    const typeLabel = active.type_name || active.type_code || "Leave";
    const isPermission = kind === "permission_hours";
    const label = isPermission
        ? `PERMISSION · ${active.start_time || "—"}${active.end_time ? "–" + active.end_time : ""}`
        : `ON LEAVE · ${typeLabel}`;
    const title = isPermission
        ? `Approved permission on ${active.start_date}${active.total_hours ? ` — ${active.total_hours}h` : ""}`
        : `Approved ${typeLabel} · ${active.start_date} → ${active.end_date}`;
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 uppercase tracking-wide whitespace-nowrap"
            title={title}
            data-testid={`att-leave-chip-${rowId}`}
            data-leave-kind={kind}
        >
            {label}
        </span>
    );
}

// Iter60.p30 (Task D) — Debounced employee search input.
function FieldSearch({ value, onChange, testid, resultCount }) {
    const [local, setLocal] = useState(value || "");
    // keep local in sync when the parent programmatically clears
    useEffect(() => { setLocal(value || ""); }, [value]);
    useEffect(() => {
        const h = setTimeout(() => {
            if ((local || "") !== (value || "")) onChange(local);
        }, 300);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local]);
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                Employee
                {resultCount !== null && (
                    <span className="ml-2 mono normal-case tracking-normal"
                          data-testid="attendance-search-results-count">
                        · {resultCount} match{resultCount === 1 ? "" : "es"}
                    </span>
                )}
            </div>
            <input
                type="text"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder="Search name or module ID…"
                className="w-64 h-9 px-2 rounded border border-[color:var(--asl-border,#E5E7EB)] bg-transparent text-sm"
                data-testid={testid}
            />
        </div>
    );
}

function FieldSel({ label, value, onChange, items, testid }) {
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="w-44" data-testid={testid}><SelectValue /></SelectTrigger>
                <SelectContent>
                    {items.map((i) => (
                        <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>{i.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}


// =========================================================================
// Iter60.p19 — Manual↔Biometric reconcile dialogs.
// =========================================================================

function fmtTimeShort(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
}

function ReplaceWithBiometricDialog({ row, onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/attendance/daily/${row.id}/biometric-preview`);
                if (!cancelled) setPreview(data);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            }
        })();
        return () => { cancelled = true; };
    }, [row.id]);

    const canApply = !!preview && preview.has_events && preview.is_manual;

    const apply = async () => {
        setBusy(true);
        try {
            await api.post(`/attendance/daily/${row.id}/replace-with-biometric`);
            toast.success("Attendance replaced with biometric data. Auto-approved.");
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="att-replace-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        <Zap className="h-5 w-5 text-emerald-600" />
                        Replace manual entry with biometric data?
                    </DialogTitle>
                    <DialogDescription>
                        The biometric device times for this day will overwrite the typed values. The row will be auto-approved and no re-review is required.
                    </DialogDescription>
                </DialogHeader>
                {err && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="att-replace-error">
                        {err}
                    </div>
                )}
                {!preview && !err && (
                    <div className="text-sm text-[color:var(--asl-muted)]">Loading preview…</div>
                )}
                {preview && (
                    <div className="rounded-lg border border-[color:var(--asl-border)] overflow-hidden text-sm">
                        <div className="grid grid-cols-3 gap-0 text-xs font-semibold uppercase tracking-wider bg-[color:var(--asl-navy-50)] text-[color:var(--asl-muted)]">
                            <div className="px-3 py-2">Field</div>
                            <div className="px-3 py-2 border-l border-[color:var(--asl-border)]">Current (Manual)</div>
                            <div className="px-3 py-2 border-l border-[color:var(--asl-border)]">After replace (Biometric)</div>
                        </div>
                        <div className="grid grid-cols-3 gap-0 border-t border-[color:var(--asl-border)]">
                            <div className="px-3 py-2 font-medium">Clock In</div>
                            <div className="px-3 py-2 mono border-l border-[color:var(--asl-border)]" data-testid="att-replace-current-in">
                                {fmtTimeShort(preview.current.first_clock_in)}
                            </div>
                            <div className="px-3 py-2 mono border-l border-[color:var(--asl-border)] font-semibold text-emerald-700" data-testid="att-replace-new-in">
                                {fmtTimeShort(preview.preview.first_clock_in)}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-0 border-t border-[color:var(--asl-border)]">
                            <div className="px-3 py-2 font-medium">Clock Out</div>
                            <div className="px-3 py-2 mono border-l border-[color:var(--asl-border)]" data-testid="att-replace-current-out">
                                {fmtTimeShort(preview.current.last_clock_out)}
                            </div>
                            <div className="px-3 py-2 mono border-l border-[color:var(--asl-border)] font-semibold text-emerald-700" data-testid="att-replace-new-out">
                                {fmtTimeShort(preview.preview.last_clock_out)}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-0 border-t border-[color:var(--asl-border)]">
                            <div className="px-3 py-2 font-medium">Approved</div>
                            <div className="px-3 py-2 border-l border-[color:var(--asl-border)]">
                                {preview.current.approved ? "Yes" : "No"}
                            </div>
                            <div className="px-3 py-2 border-l border-[color:var(--asl-border)] text-emerald-700 font-semibold">
                                Yes (auto)
                            </div>
                        </div>
                        <div className="px-3 py-2 text-xs bg-[color:var(--asl-navy-50)] border-t border-[color:var(--asl-border)]">
                            {preview.event_count} biometric event{preview.event_count === 1 ? "" : "s"} on {row.work_date}. Late minutes and status will be recomputed from the shift rule.
                        </div>
                    </div>
                )}
                {preview && !preview.is_manual && (
                    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        This row is already biometric — nothing to replace.
                    </div>
                )}
                {preview && preview.is_manual && !preview.has_events && (
                    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        No biometric events found for this employee/date. Nothing to replace with.
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="att-replace-cancel">Cancel</Button>
                    <Button
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        onClick={apply}
                        disabled={busy || !canApply}
                        data-testid="att-replace-confirm"
                    >
                        {busy ? "Replacing…" : "Replace with Biometric"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DeleteManualDialog({ row, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const valid = reason.trim().length >= 5;

    const apply = async () => {
        setBusy(true);
        try {
            await api.delete(`/attendance/daily/${row.id}/manual`, {
                data: { reason: reason.trim() },
            });
            toast.success("Manual entry deleted permanently.");
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="att-delete-manual-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[color:var(--asl-crimson)]">
                        <Trash2 className="h-5 w-5" />
                        Delete manual attendance entry?
                    </DialogTitle>
                    <DialogDescription>
                        This will permanently delete the record from <span className="mono font-semibold">{row.work_date}</span>. This action cannot be undone. If biometric events later arrive for this day, a fresh biometric-based row will be produced automatically.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="att-delete-reason" className="text-xs">
                        Reason <span className="text-rose-600">*</span>
                        <span className="text-[10px] text-[color:var(--asl-muted)]"> (min 5 characters)</span>
                    </Label>
                    <textarea
                        id="att-delete-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. duplicate manual entry, biometric arrived later"
                        rows={3}
                        className="w-full rounded-md border border-[color:var(--asl-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--asl-navy)]"
                        data-testid="att-delete-reason"
                        autoFocus
                    />
                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                        The reason is recorded in the audit log for accountability.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="att-delete-manual-cancel">Cancel</Button>
                    <Button
                        className="bg-[color:var(--asl-crimson)] text-white hover:brightness-95"
                        onClick={apply}
                        disabled={busy || !valid}
                        data-testid="att-delete-manual-confirm"
                    >
                        {busy ? "Deleting…" : "Delete permanently"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkReplaceDialog({ rowIds, onClose, onDone }) {
    const [busy, setBusy] = useState(false);

    const apply = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/daily/bulk-replace-with-biometric", { row_ids: rowIds });
            const c = data.counts || {};
            if (c.replaced > 0) {
                toast.success(`Replaced ${c.replaced} row${c.replaced === 1 ? "" : "s"} with biometric data. Auto-approved.`);
            }
            if (c.skipped > 0) {
                toast.info(`${c.skipped} row${c.skipped === 1 ? "" : "s"} skipped (no biometric events or not manual).`);
            }
            if (c.errors > 0) {
                toast.error(`${c.errors} row${c.errors === 1 ? "" : "s"} failed. See console for details.`);
                console.warn("bulk-replace errors:", data.errors);
            }
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="att-bulk-replace-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        <Zap className="h-5 w-5 text-emerald-600" />
                        Replace {rowIds.length} manual row{rowIds.length === 1 ? "" : "s"} with biometric?
                    </DialogTitle>
                    <DialogDescription>
                        For each selected manual row, the biometric device times (if any) will overwrite the typed values and the row will be auto-approved. Rows without biometric events for that day are skipped — none of them fail the batch.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="att-bulk-replace-cancel">Cancel</Button>
                    <Button
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        onClick={apply}
                        disabled={busy || rowIds.length === 0}
                        data-testid="att-bulk-replace-confirm"
                    >
                        {busy ? "Replacing…" : `Replace ${rowIds.length}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
